/**
 * Mirrors the backend DTO records exactly. Field names are the Java record
 * component names — do not "tidy" them.
 *
 * Derived from `openapi/openapi.json` (Axis API v1) after the Phase 1 admin
 * work landed. Regenerate the machine types with `pnpm api:sync && pnpm api:gen`;
 * this file stays as the readable, hand-checked surface the feature code imports.
 *
 * The audit/notification `DayCount` collision that used to live here is FIXED —
 * the backend renamed the audit one to `AuditDayCount`, so the two schemas are
 * distinct in `openapi.json` and the generated types are correct for both.
 */

// ── Envelopes ───────────────────────────────────────────────────────────────

/** shared/web/PageResponse — offset pagination. */
export interface PageResponse<T> {
  content: T[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

/** shared/web/CursorPage — keyset pagination. `nextCursor` is opaque. */
export interface CursorPage<T> {
  content: T[]
  nextCursor?: string | null
  hasMore: boolean
}

// ── Enum codes (CodedEnum `code` values, never the Java constant names) ──────

export type UserStatusCode = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DEACTIVATED' | 'UNKNOWN'
export type ClearanceCode =
  | 'PUBLIC' | 'INTERNAL' | 'RESTRICTED' | 'SECRET' | 'TOP_SECRET' | 'UNKNOWN'
export type ScopeLevelCode =
  | 'NATIONAL' | 'COUNTY' | 'CONSTITUENCY' | 'WARD' | 'STATION' | 'UNKNOWN'
export type AuditCategoryCode =
  | 'SECURITY' | 'DATA' | 'CONFIGURATION' | 'ACCESS' | 'SYSTEM' | 'UNKNOWN'
export type AuditOutcomeCode = 'SUCCESS' | 'FAILURE' | 'DENIED' | 'UNKNOWN'
export type NotificationStatusCode = 'PENDING' | 'SENT' | 'FAILED' | 'UNKNOWN'
export type NotificationChannelCode =
  | 'EMAIL' | 'SMS' | 'PUSH' | 'WHATSAPP' | 'IN_APP' | 'UNKNOWN'
export type NotificationTypeCode =
  | 'USER_INVITATION' | 'PASSWORD_RESET' | 'SECURITY_ALERT'
  | 'SYSTEM_ANNOUNCEMENT' | 'AD_HOC' | 'UNKNOWN'
export type RevokeReasonCode =
  | 'LOGOUT' | 'ROTATED' | 'REUSE_DETECTED' | 'PASSWORD_CHANGED' | 'ADMIN_REVOKED'
  | 'EXPIRED' | 'USER_DEACTIVATED' | 'UNKNOWN'

/**
 * Every one of the above may arrive as `UNKNOWN` — that is the CodedEnum
 * sentinel for a code this build does not recognise, i.e. the backend is newer
 * than the bundle. Render it, never crash on it.
 */
export const isUnknownCode = (v: string | null | undefined) => v === 'UNKNOWN'

// ── Auth ────────────────────────────────────────────────────────────────────

/** auth/dto/LoginResponse */
export interface LoginResponse {
  accessToken: string
  expiresIn?: number
  tokenType?: string
  mustChangePassword?: boolean
}

/** auth/dto/SessionResponse — your own sessions. Revoked by familyId. */
export interface SessionResponse {
  familyId: string
  deviceLabel?: string | null
  ip?: string | null
  userAgent?: string | null
  issuedAt: string
  expiresAt: string
  current: boolean
}

/** auth AdminSessionController — every user's sessions. `session:read-any`. */
export interface AdminSessionResponse {
  familyId: string
  userId: string
  userEmail?: string | null
  userName?: string | null
  deviceLabel?: string | null
  userAgent?: string | null
  ip?: string | null
  issuedAt: string
  /** Rotation-granular: the newest row in the family, not a per-request touch. */
  lastSeenAt?: string | null
  expiresAt: string
  active: boolean
  revokedAt?: string | null
  revokeReason?: RevokeReasonCode | null
  /** Belongs to the calling user. */
  mine: boolean
  /** This exact browser session. */
  current: boolean
}

export interface SessionSummaryResponse {
  activeSessions: number
  activeUsers: number
  sessionsToday: number
  revokedLast7Days: number
  byRevokeReason: Record<string, number>
  topDevices: { label: string; count: number }[]
  generatedAt: string
}

export interface RevokeAllResponse {
  userId: string
  revoked: number
}

/** user InvitationController */
export interface InvitationIssuedResponse {
  userId: string
  expiresAt: string
  /** The PRIMARY channel — the phone number is the account identity. */
  smsSent: boolean
  /** Secondary, and only attempted when the account has an email at all. */
  emailSent: boolean
  notificationId?: string | null
  deliveryError?: string | null
  /** Present only when `security.invitation-return-token` is on. Never in prod. */
  token?: string | null
  /** The full link, same gate as `token`. This is the credential — see InviteLinkPanel. */
  inviteLink?: string | null
}

export interface InvitationStatusResponse {
  valid: boolean
  reason: 'VALID' | 'NOT_FOUND' | 'EXPIRED' | 'CONSUMED' | 'REVOKED' | string
  /** Masked — this endpoint is anonymous. */
  email?: string | null
  expiresAt?: string | null
}

// ── User ────────────────────────────────────────────────────────────────────

/** Role chip carried on a user, resolved server-side in one batch query. */
export interface RoleRef {
  id: string
  name: string
  displayName: string
  colour?: string | null
  expiresAt?: string | null
}

/** user/dto/UserResponse */
export interface UserResponse {
  id: string
  /** The account identity, E.164. Unique, and what login takes. */
  phone: string
  /** ISO 3166-1 alpha-2 of `phone`. */
  phoneCountry: string
  /** Optional contact field since the phone-identity change. Not unique, not an identity. */
  email?: string | null
  name: string
  /** Whether a mobile PIN is set. The PIN itself is never returned. */
  pinSet: boolean
  status: UserStatusCode
  scopeLevel: ScopeLevelCode
  scopeUnitId?: string | null
  clearance: ClearanceCode
  mustChangePassword: boolean
  bootstrap: boolean
  lastLoginAt?: string | null
  passwordChangedAt?: string | null
  failedLoginCount: number
  lockedUntil?: string | null
  avatarFileId?: string | null
  locale?: string | null
  timezone?: string | null
  roles: RoleRef[]
  version: number
  createdAt: string
  updatedAt?: string | null
}

export interface UserSummaryResponse {
  total: number
  byStatus: Record<string, number>
  byScopeLevel: Record<string, number>
  byClearance: Record<string, number>
  neverLoggedIn: number
  lockedNow: number
  createdLast7Days: number
  activeLast24Hours: number
  pendingInvitations: number
  generatedAt: string
}

export interface CreateUserRequest {
  /** ISO 3166-1 alpha-2. Required — a bare number cannot be normalised. */
  phoneCountry: string
  /** As typed; the server normalises to E.164. */
  phone: string
  email?: string
  name: string
  scopeLevel: string
  scopeUnitId?: string | null
  clearance: string
  roleIds?: string[]
}

export interface UpdateUserRequest {
  name?: string
  /** Must be sent together with `phone`, or neither. */
  phoneCountry?: string
  phone?: string
  /** '' clears the email; undefined leaves it alone. */
  email?: string
  locale?: string
}

/** PUT /users/me/pin and PUT /users/{id}/pin. */
export interface SetPinRequest {
  /** Exactly four digits. */
  pin: string
  /** Required when setting your OWN pin; ignored when an admin sets somebody else's. */
  currentPassword?: string
}

// ── RBAC ────────────────────────────────────────────────────────────────────

/** rbac/api/EffectiveAuthorities — NOT part of /users/me. Separate endpoint. */
export interface EffectiveAuthorities {
  userId: string
  authorities: string[]
  fingerprint: string
}

/** rbac PermissionResponse — `dangerous`, not `isDangerous`. */
export interface PermissionResponse {
  id: string
  module: string
  resource?: string | null
  action: string
  displayName: string
  description?: string | null
  dangerous: boolean
  authority: string
}

/** GET /rbac/permissions?grouped=true */
export interface PermissionModuleResponse {
  module: string
  displayName: string
  description?: string | null
  order: number
  permissions: PermissionResponse[]
}

/** rbac/dto/RoleResponse — `system`/`assignable`, not `isSystem`. */
export interface RoleResponse {
  id: string
  name: string
  displayName: string
  description?: string | null
  system: boolean
  assignable: boolean
  colour?: string | null
  memberCount: number
  permissionCount: number
  version: number
  createdAt: string
  permissions: PermissionResponse[]
}

/** GET /rbac/users/{userId}/roles — names, not bare UUIDs. */
export interface UserRoleResponse {
  roleId: string
  name: string
  displayName: string
  colour?: string | null
  system: boolean
  assignedAt: string
  assignedBy?: string | null
  expiresAt?: string | null
  active: boolean
}

export interface CreateRoleRequest {
  name: string
  displayName: string
  description?: string
  colour?: string
}

export interface UpdateRoleRequest {
  displayName?: string
  description?: string
  colour?: string
}

export interface CloneRoleRequest {
  name: string
  displayName: string
}

export interface ReplaceGrantsRequest {
  permissionIds: string[]
}

/** POST /rbac/roles/{id}/permissions/preview — computed, not persisted. */
export interface GrantChangePreviewResponse {
  added: PermissionResponse[]
  removed: PermissionResponse[]
  affectedUserCount: number
  dangerousAdded: string[]
  refusedByEscalationGuard: boolean
  refusalReason?: string | null
}

// ── Audit ───────────────────────────────────────────────────────────────────

/** A single field mutation. `from`/`to` are rendered strings; null = unset. */
export interface FieldChange {
  field: string
  from?: string | null
  to?: string | null
}

/** Code + display name, so the UI never has to own the label. */
export interface CodeLabel {
  code: string
  displayName: string
}

export interface AuditEntryResponse {
  id: string
  occurredAt: string
  actorId?: string | null
  /** Denormalised at write time — the actor's name AS IT WAS. Never re-resolved. */
  actorLabel?: string | null
  action: string
  category?: CodeLabel | null
  outcome?: CodeLabel | null
  entityType?: string | null
  entityId?: string | null
  entityLabel?: string | null
  details?: Record<string, unknown> | null
  changes?: FieldChange[] | null
  ip?: string | null
  userAgent?: string | null
  requestId?: string | null
}

/** audit `AuditDayCount` — `(day, count)`. */
export interface AuditDayCount {
  day: string
  count: number
}

export interface AuditSummaryResponse {
  total: number
  today: number
  last7Days: number
  failures: number
  distinctActors: number
  byCategory: Record<string, number>
  byOutcome: Record<string, number>
  topActions: { action: string; count: number }[]
  daily: AuditDayCount[]
  generatedAt: string
}

export interface AuditFacetsResponse {
  actions: string[]
  entityTypes: string[]
  categories: string[]
  outcomes: string[]
  topActors: { id: string; label: string; count: number }[]
}

export interface AuditExportResponse {
  fileId: string
  filename: string
  rows: number
  truncated: boolean
  sizeBytes: number
}

// ── Notification ────────────────────────────────────────────────────────────

export interface NotificationSummaryResponse {
  id: string
  type?: NotificationTypeCode | null
  channel: NotificationChannelCode
  recipient: string
  recipientUserId?: string | null
  subject: string
  status: NotificationStatusCode
  attempts: number
  queuedAt?: string | null
  sentAt?: string | null
  lastAttemptAt?: string | null
  error?: string | null
  entityType?: string | null
  entityId?: string | null
  requestId?: string | null
  createdAt: string
}

/** Adds the body. Invitation and reset tokens are redacted server-side. */
export interface NotificationDetailResponse extends NotificationSummaryResponse {
  bodyHtml?: string | null
  createdBy?: string | null
}

/** notification DayCount — `(day, sent, failed)`. */
export interface NotificationDayCount {
  day: string
  sent: number
  failed: number
}

export interface NotificationSummaryStats {
  total: number
  sent: number
  failed: number
  pending: number
  today: number
  last7Days: number
  /** 0 when there is nothing to divide by — do not render NaN. */
  successRate: number
  byChannel: Record<string, number>
  byType: Record<string, number>
  daily: NotificationDayCount[]
  topFailures: { reason: string; count: number }[]
  generatedAt: string
}

export interface FacetCount {
  value: string
  count: number
}

export interface NotificationFacets {
  types: FacetCount[]
  channels: FacetCount[]
  statuses: FacetCount[]
}

// ── Settings ────────────────────────────────────────────────────────────────

/** SettingResponse — values are STRINGS on the wire, whatever `valueType` says. */
export interface SettingResponse {
  key: string
  category: string
  valueType: string
  defaultValue?: string | null
  value?: string | null
  secret: boolean
  displayName: string
  description?: string | null
  readOnly: boolean
  /**
   * Prompt 31. All four are absent on an older backend, and every one of them makes the
   * screen degrade rather than break.
   *
   * `validation` is the definition's own JSON blob (`{"min":…}`, `{"pattern":…}`,
   * `{"format":"url"}`), so the browser can refuse a bad value before the round trip
   * instead of surfacing a 400 as the first feedback.
   *
   * `source` and `effectiveValue` are what make an environment fallback legible: "not
   * set here — using AXIS_APP_WEB_URL" is an answer; a blank box next to a working
   * system is a mystery. `UNSET` means no row, no environment value and no default —
   * the state that shipped a broken invitation link.
   */
  validation?: string | null
  requiredAuthority?: string | null
  source?: 'VALUE' | 'ENVIRONMENT' | 'DEFAULT' | 'UNSET' | null
  /** Never populated for a secret. */
  effectiveValue?: string | null
}

/** GET /settings returns Map<category, SettingResponse[]> — not a flat array. */
export type SettingsByCategory = Record<string, SettingResponse[]>


// ── Geography (Phase 2) ─────────────────────────────────────────────────────

export type UnitTypeCode =
  | 'COUNTRY' | 'COUNTY' | 'CONSTITUENCY' | 'WARD' | 'POLLING_STATION' | 'UNKNOWN'
export type UnitStatusCode =
  | 'ACTIVE' | 'MERGED' | 'DISSOLVED' | 'PROPOSED' | 'UNKNOWN'
export type StationLocationTypeCode =
  | 'SCHOOL' | 'CHURCH' | 'MOSQUE' | 'SOCIAL_HALL' | 'CHIEF_CAMP' | 'MARKET'
  | 'TENT' | 'OTHER' | 'UNKNOWN'
export type ImportJobStatusCode =
  | 'PENDING' | 'VALIDATING' | 'VALIDATED' | 'APPLYING' | 'COMPLETED'
  | 'FAILED' | 'CANCELLED' | 'UNKNOWN'

/**
 * The unit type order IS the hierarchy. Used for the "what can I create under
 * this?" logic and for sorting — never derived from a string compare.
 */
export const UNIT_TYPE_ORDER: UnitTypeCode[] = [
  'COUNTRY', 'COUNTY', 'CONSTITUENCY', 'WARD', 'POLLING_STATION',
]

export const UNIT_TYPE_LABEL: Record<string, string> = {
  COUNTRY: 'Country',
  COUNTY: 'County',
  CONSTITUENCY: 'Constituency',
  WARD: 'Ward',
  POLLING_STATION: 'Polling station',
  UNKNOWN: 'Unknown',
}

export const UNIT_TYPE_PLURAL: Record<string, string> = {
  COUNTRY: 'Countries',
  COUNTY: 'Counties',
  CONSTITUENCY: 'Constituencies',
  WARD: 'Wards',
  POLLING_STATION: 'Polling stations',
  UNKNOWN: 'Unknown',
}

/** The type a child of `parent` must be, or null at the bottom of the tree. */
export function childTypeOf(parent: string | null | undefined): UnitTypeCode | null {
  const i = UNIT_TYPE_ORDER.indexOf((parent ?? '') as UnitTypeCode)
  if (i < 0 || i === UNIT_TYPE_ORDER.length - 1) return null
  return UNIT_TYPE_ORDER[i + 1] ?? null
}

/**
 * Which geometry a unit type carries.
 *
 * A county, constituency or ward is an **area** — its authoritative geometry is a
 * polygon and its pin is the polygon's centroid. A polling station is a **place** —
 * its authoritative geometry is a coordinate pair and it has no polygon; the
 * backend rejects a boundary on one outright ("Polling stations have no boundary").
 *
 * The backend now serves `geometryKind` on every row and in `GET /geography/unit-types`.
 * **Prefer the served value.** This helper stays as the offline fallback — for a
 * `UnitTreeNode`, which carries no `geometryKind`, and for anywhere a unit type is
 * known before a row has been fetched (the create dialog picks its map mode from the
 * parent's type before the child exists).
 */
export type GeometryKind = 'POINT' | 'BOUNDARY'

export function geometryKindOf(unitType: string | null | undefined): GeometryKind {
  return unitType === 'POLLING_STATION' ? 'POINT' : 'BOUNDARY'
}

/** Where a unit's centroid came from. `DERIVED` means the boundary computed it. */
export type CentroidSourceCode = 'DERIVED' | 'MANUAL' | 'IMPORTED' | 'UNKNOWN'

export type RiskLevelCode = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'UNKNOWN'
export type MoodTrendCode = 'RISING' | 'STABLE' | 'FALLING' | 'UNKNOWN'

/** Severity order, worst first. Alphabetical sorting gets this exactly backwards. */
export const RISK_ORDER: RiskLevelCode[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']

export const RISK_LABEL: Record<string, string> = {
  LOW: 'Low', MEDIUM: 'Medium', HIGH: 'High', CRITICAL: 'Critical', UNKNOWN: 'Unknown',
}

/**
 * The intelligence overlay on a unit row — turnout, mood, risk and agent.
 *
 * Served from `geography_unit_overlay`, a projection table geography owns and other
 * features publish into through `UnitOverlayPort`. The assessments are deliberately
 * NOT columns on `geography_unit` (geography plan §2.3: a mood score is a fact about
 * an assessment, not about a place).
 *
 * `overlay` is **null until something publishes**, and individual fields stay null
 * until their facet is published. Both mean "nobody has looked" — never zero, and
 * never low. See `features/geography/IntelCells.tsx`.
 */
export interface UnitOverlay {
  turnoutPct?: number | null
  turnoutYear?: number | null
  /** `OWN` — this unit's own statistic row. `DERIVED` — summed from descendants. */
  turnoutBasis?: 'OWN' | 'DERIVED' | 'UNKNOWN' | null
  moodIndex?: number | null
  moodTrend?: MoodTrendCode | null
  moodAssessedAt?: string | null
  riskLevel?: RiskLevelCode | null
  riskAssessedAt?: string | null
  agentUserId?: string | null
  agentName?: string | null
  agentPhone?: string | null
  agentCheckedInAt?: string | null
  /** Non-stations only: how many stations beneath this unit have an agent. */
  stationsWithAgent?: number | null
  stationsTotal?: number | null
}

/**
 * Polling-station facility detail, from `geography_station_detail`.
 *
 * Sent inside `station` on create and update, and returned on
 * `UnitDetailResponse.station`. On update a null field means **leave alone**, so a
 * partial edit does not blank the rest — send only what changed.
 */
export interface StationDetail {
  locationType?: StationLocationTypeCode | null
  streamCount?: number | null
  hasPower?: boolean | null
  hasNetwork?: boolean | null
  disabledAccess?: boolean | null
  elevationM?: number | null
  notes?: string | null
}

export interface UnitResponse {
  id: string
  unitType: UnitTypeCode
  code: string
  name: string
  parentId?: string | null
  parentName?: string | null
  /** ltree path, e.g. `KE.047.274.1370.0123`. Dot-separated, root first. */
  path: string
  depth: number
  region?: string | null
  latitude?: number | null
  longitude?: number | null
  areaSqKm?: number | null
  population?: number | null
  registeredVoters: number
  /** Maintained rollup, not a live count. Reconciled nightly. */
  stationCount: number
  status: UnitStatusCode
  mergedIntoId?: string | null
  attributes?: Record<string, unknown> | null
  version: number
  createdAt: string
  updatedAt?: string | null

  /** `POINT` or `BOUNDARY`, from the unit type. */
  geometryKind?: GeometryKind | null
  /** `boundary IS NOT NULL`, answered without loading any geometry. */
  hasBoundary?: boolean | null
  centroidSource?: CentroidSourceCode | null
  /** Null until something publishes an assessment. */
  overlay?: UnitOverlay | null
}

export interface AncestorRef {
  id: string
  unitType: UnitTypeCode
  code: string
  name: string
}

export interface UnitDetailResponse {
  unit: UnitResponse
  /**
   * NEAREST-FIRST, excluding the unit itself: ward, constituency, county, country.
   * `ancestors[0]` is the immediate parent. Reverse it for anything a person reads —
   * a breadcrumb or a path runs root-first.
   */
  ancestors: AncestorRef[]
  childCounts: { children: number; stations: number }
  /**
   * Register statistics, newest year first. Written by
   * `PUT /units/{id}/statistics/{year}` or the statistics import; a unit with none
   * returns `[]`, which is the normal state until someone loads IEBC results.
   */
  statistics: {
    electionYear: number
    registeredVoters?: number | null
    votesCast?: number | null
    turnoutPct?: number | null
    rejectedVotes?: number | null
    source?: string | null
  }[]
  /** Station facility detail. Null for anything that is not a polling station. */
  station?: StationDetail | null
}

/** Lazy tree node. `children` is only populated within the requested depth. */
export interface UnitTreeNode {
  id: string
  unitType: UnitTypeCode
  code: string
  name: string
  status: UnitStatusCode
  registeredVoters: number
  stationCount: number
  hasChildren: boolean
  children?: UnitTreeNode[] | null
}

export interface NearbyResponse {
  id: string
  code: string
  name: string
  path: string
  distanceMetres: number
}

/**
 * `GET /geography/summary` returns a bare `Map<String,Object>` server-side, so
 * OpenAPI types it as an untyped object and `api:gen` produces nothing usable.
 * This is the actual shape, read from `UnitQueryService.summary`.
 */
export interface GeographySummary {
  total: number
  byType: Record<string, number>
  byStatus: Record<string, number>
  /** `UNASSESSED` is a first-class bucket, not an omission — in month one it is the
   *  headline number. */
  byRisk?: Record<string, number> | null
  agentCoverage?: { stationsWithAgent: number; stationsTotal: number } | null
  turnoutCoverage?: { unitsWithTurnout: number; unitsTotal: number } | null
}

export interface CreateUnitRequest {
  unitType: string
  code: string
  name: string
  parentId?: string | null
  region?: string | null
  latitude?: number | null
  longitude?: number | null
  registeredVoters?: number | null
  /**
   * **Deprecated** — use `station.locationType`. The backend still accepts it and
   * 400s if the two disagree; it is removed next release. Nothing in this client
   * sends it any more.
   */
  locationType?: string | null
  population?: number | null
  station?: StationDetail | null
  attributes?: Record<string, unknown> | null
}

export interface UpdateUnitRequest {
  name?: string
  region?: string | null
  latitude?: number | null
  longitude?: number | null
  registeredVoters?: number | null
  population?: number | null
  boundaryFileId?: string | null
  station?: StationDetail | null
  attributes?: Record<string, unknown> | null
}

export interface ChangeUnitStatusRequest {
  status: string
  /** Required by the backend when status is MERGED. */
  mergedIntoId?: string | null
}

export interface ImportRequest {
  fileId: string
  unitType: string
  dryRun: boolean
}

/** One rejected row. The backend emits a bare map with these three keys. */
export interface ImportInvalidRow {
  row?: number
  column?: string | null
  message?: string
}

export interface ImportReport {
  totalRows: number
  valid: number
  /** The TRUE count. `invalid` below is capped server-side (default 500). */
  invalidCount: number
  invalid: ImportInvalidRow[]
  wouldCreate: number
  wouldUpdate: number
  /**
   * Non-fatal observations — a ward boundary not fully inside its constituency,
   * for instance. Boundary files from different sources disagree at the edges, so
   * these are reported and not rejected. Capped like `invalid`.
   */
  warnings?: string[] | null
}

export interface ImportJobResponse {
  id: string
  fileId: string
  unitType: UnitTypeCode
  status: ImportJobStatusCode
  dryRun: boolean
  totalRows?: number | null
  processedRows: number
  createdCount: number
  updatedCount: number
  invalidCount?: number | null
  report?: ImportReport | null
  error?: string | null
  startedAt?: string | null
  finishedAt?: string | null
}

/** A job in one of these states is still moving — keep polling. */
export const IMPORT_JOB_ACTIVE: ImportJobStatusCode[] = [
  'PENDING', 'VALIDATING', 'APPLYING',
]

// ── Unit-type catalogue, code availability, import templates ────────────────

/**
 * `GET /geography/unit-types` — the server's copy of the hierarchy order, the
 * labels and the geometry rules. Prefer this over the client-side constants; they
 * remain for the offline fallback and for a type known before any row is fetched.
 */
export interface UnitTypeInfo {
  code: UnitTypeCode
  label: string
  pluralLabel: string
  parentType?: UnitTypeCode | null
  childType?: UnitTypeCode | null
  geometryKind?: GeometryKind | null
  /** False for COUNTRY — the root is created by its own endpoint. */
  creatable: boolean
  importable: boolean
  /** 1..5, equal to `nlevel(path)`. */
  depth: number
}

/**
 * `GET /geography/units/code-available`.
 *
 * `LTREE_COLLISION` is the interesting one: the code itself is free, but its ltree
 * label collides with a sibling's, because the path builder replaces every
 * non-alphanumeric with `_` — so `04.701` and `04-701` become the same label.
 */
export interface CodeAvailability {
  available: boolean
  existingUnitId?: string | null
  existingUnitName?: string | null
  reason?: 'TAKEN' | 'LTREE_COLLISION' | string | null
}

export interface ImportColumnSpec {
  name: string
  type: string
  required: boolean
  description: string
  example?: string | null
}

/**
 * `GET /geography/import/template/columns`. The CSV template and this spec are
 * generated from one `ImportTemplates` class server-side, so the reference the UI
 * renders cannot drift from what the parser accepts.
 */
export interface ImportTemplateSpec {
  unitType: UnitTypeCode
  parentUnitType?: UnitTypeCode | null
  columns: ImportColumnSpec[]
  notes: string[]
}

export interface BoundaryImportRequest {
  fileId: string
  /** Stations have no boundary, so the backend restricts this to the area types. */
  unitType: 'COUNTY' | 'CONSTITUENCY' | 'WARD'
  /** The GeoJSON feature property holding the unit code — often `ward_code`, `CODE`… */
  codeProperty: string
  dryRun: boolean
}

// ── Statistics and the overlay write surface ────────────────────────────────

/**
 * `PUT /geography/units/{id}/statistics/{electionYear}`.
 *
 * `turnoutPct` is computed from the two counts when omitted; supplying one that
 * disagrees with the computed value by more than 0.5 points is a 400 rather than a
 * silent preference for one of them.
 */
export interface UnitStatisticRequest {
  registeredVoters?: number | null
  votesCast?: number | null
  turnoutPct?: number | null
  rejectedVotes?: number | null
  source?: string | null
}

export interface MoodAssessmentRequest {
  /** 0–100 favourability. */
  index: number
  trendCode: MoodTrendCode
  assessedAt?: string | null
  source?: string | null
}

export interface RiskAssessmentRequest {
  levelCode: RiskLevelCode
  assessedAt?: string | null
  source?: string | null
}

/**
 * Stations only — a ward's agent column is coverage, not a person.
 *
 * The user id is the whole input. Name and phone are NOT sent: they belong to the
 * account, and the server reads them from it on every publish, so the station's row
 * cannot drift from User Management.
 */
export interface AgentAssignmentRequest {
  userId: string
  assignedAt?: string | null
  checkedInAt?: string | null
}

/** Minimal GeoJSON, as the boundary endpoints exchange it. */
export interface GeoJsonGeometry {
  type: string
  coordinates: unknown
}
export interface GeoJsonObject {
  type: string
  geometry?: GeoJsonGeometry
  coordinates?: unknown
  features?: { geometry?: GeoJsonGeometry }[]
  crs?: { properties?: { name?: string } }
  [key: string]: unknown
}

// ── Storage (used by the geography import) ──────────────────────────────────

export interface ReserveRequest {
  filename: string
  contentType: string
  sizeBytes: number
  purpose: string
  ownerEntityType?: string | null
  ownerEntityId?: string | null
}

export interface ReserveResponse {
  fileId: string
  uploadUrl: string
}

// ════════════════════════════════════════════════════════════════════════════
// PHASE 3 — voter · integrations
//
// Read from the SHIPPED Java records after prompts 15–17 and 22, not from the
// plan. Two conventions differ from what the plan sketched and are load-bearing:
//
//   1. Enum-valued fields are `xxxCode` STRINGS (`supportBandCode`), because
//      `api/*` may not expose `domain` enums — the same rule that gives
//      `UnitSummary.statusCode` its name.
//   2. Several voter endpoints serialise JPA ENTITIES directly rather than a
//      DTO (verifications, turnout, tags, placement history). Those carry
//      BaseEntity's `id`/`version`/`createdAt`/`updatedAt`/`createdBy`/
//      `updatedBy` and Jackson renders their enums by constant name. Modelled
//      here as `*Entity` types so the difference stays visible; prompt 22 §7
//      lists replacing them with DTOs as outstanding.
// ════════════════════════════════════════════════════════════════════════════

// ── voter · codes ───────────────────────────────────────────────────────────

export type GenderCode = 'MALE' | 'FEMALE' | 'OTHER' | 'UNDISCLOSED'
export type AgeBandCode = 'A18_24' | 'A25_34' | 'A35_44' | 'A45_54' | 'A55_64' | 'A65_PLUS'
export type RegistrationStatusCode =
  'REGISTERED' | 'PENDING' | 'TRANSFERRED_OUT' | 'DECEASED' | 'INELIGIBLE' | 'SUSPENDED'
export type VoterStatusCode = 'ACTIVE' | 'MERGED' | 'ERASED'
export type SupportBandCode =
  'OPPOSED' | 'LEAN_OPPOSED' | 'UNDECIDED' | 'LEAN_SUPPORT' | 'SUPPORTER' | 'UNASSESSED'
export type SupportConfidenceCode = 'UNASSESSED' | 'INFERRED' | 'REPORTED' | 'VERIFIED'
export type VerificationStatusCode =
  'UNVERIFIED' | 'PHONE_VERIFIED' | 'FIELD_VERIFIED' | 'ID_SIGHTED' | 'IEBC_MATCHED' | 'FAILED'
export type ContactChannelCode =
  'DOOR_TO_DOOR' | 'PHONE_CALL' | 'SMS' | 'WHATSAPP' | 'EMAIL' | 'RALLY' | 'MEETING'
  | 'USSD' | 'WEB_FORM' | 'REFERRAL' | 'OTHER'
export type ContactOutcomeCode =
  'REACHED' | 'NO_ANSWER' | 'WRONG_NUMBER' | 'REFUSED' | 'MOVED' | 'DECEASED'
  | 'CALLBACK_REQUESTED' | 'HOSTILE' | 'UNDELIVERABLE'
export type ConsentPurposeCode =
  'REGISTER_HOLDING' | 'CONTACT_SMS' | 'CONTACT_CALL' | 'CONTACT_WHATSAPP' | 'CONTACT_EMAIL'
  | 'PROGRAM_ENROLMENT' | 'TRANSFER_REQUEST' | 'SENSITIVE_ATTRIBUTES' | 'RESEARCH'
export type ConsentMethodCode =
  'SIGNATURE' | 'OTP' | 'VERBAL_WITNESSED' | 'WEB_FORM' | 'USSD' | 'IMPORT_ATTESTED'
export type SuppressionReasonCode =
  'VOTER_REQUEST' | 'DECEASED' | 'WRONG_PERSON' | 'HOSTILE' | 'REGULATORY' | 'BOUNCED_REPEATEDLY'
export type RiskBandCode = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
export type ChannelKindCode = 'PHONE' | 'ALT_PHONE' | 'WHATSAPP' | 'EMAIL' | 'USSD_MSISDN'
export type SentimentCode = 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' | 'HOSTILE'

// ── voter · responses ───────────────────────────────────────────────────────

/** voter/dto/VoterResponse — the list row. Carries no PII by design. */
export interface VoterResponse {
  id: string
  displayName: string
  pollingStationId?: string | null
  wardId?: string | null
  supportLevel: number
  supportBandCode: SupportBandCode
  confidenceCode: SupportConfidenceCode
  verificationStatusCode: VerificationStatusCode
  statusCode: VoterStatusCode
  doNotContact: boolean
  doNotContactReasonCode?: SuppressionReasonCode | null
  /** The intel projection. Null until a collector has produced anything. */
  overlay?: VoterOverlay | null
  /**
   * Household membership, on the list row so `?householdId=` returns a usable member
   * list: a household is who lives there AND what they are to each other, and names
   * without roles are only half of it.
   */
  householdId?: string | null
  householdRoleCode?: string | null
  version: number
  createdAt: string
  updatedAt?: string | null
  /** Resolved server-side (prompt 29). Absent on an older backend. */
  pollingStationName?: string | null
  /**
   * The transfer marker — **two independent facts**, deliberately not one enum.
   *
   * `open*` is the request in flight, if any. `last*` is sticky evidence that the IEBC
   * acted and we wrote it down; a later withdrawal or rejection never clears it, and
   * only another recording or a reversal changes it.
   *
   * Collapsing the two into a single `NONE | OPEN | RECORDED` loses exactly what the
   * register is for: a voter relocated in March who raises and withdraws a request in
   * June would end up reading "no transfer", having genuinely been relocated.
   *
   * `kind` matters because prompt 28 gave RECORD a second meaning — a recorded
   * REGISTRATION put someone ON the roll, which is a different sentence from "we got
   * this person moved", and counting them together over-reports.
   */
  openTransferReference?: string | null
  openTransferKind?: RequestKindCode | null
  lastTransferReference?: string | null
  lastTransferRecordedAt?: string | null
  lastTransferKind?: RequestKindCode | null
}

export interface VoterOverlay {
  itemCount: number
  lastItemAt?: string | null
  openAlertCount: number
  riskBandCode?: RiskBandCode | null
  credibilityGrade?: string | null
}

/** voter/dto/VoterDetailResponse. */
export interface VoterDetailResponse {
  id: string
  voterNumber?: string | null
  firstName: string
  middleName?: string | null
  lastName: string
  displayName: string
  genderCode?: GenderCode | null
  birthYear?: number | null
  ageBandCode?: AgeBandCode | null
  preferredLanguageCode?: string | null
  specialNeeds?: string[] | null
  literacyCode?: string | null
  countyId?: string | null
  constituencyId?: string | null
  wardId?: string | null
  pollingStationId?: string | null
  unitPath?: string | null
  registrationStatusCode: RegistrationStatusCode
  registeredAt?: string | null
  residenceWardId?: string | null
  residenceDescription?: string | null
  residenceLatitude?: number | null
  residenceLongitude?: number | null
  residenceSourceCode?: string | null
  residenceConfidence?: number | null
  householdId?: string | null
  householdRoleCode?: string | null
  supportLevel: number
  supportBandCode: SupportBandCode
  confidenceCode: SupportConfidenceCode
  supportAssessedAt?: string | null
  assignedMobilizerId?: string | null
  lastContactedAt?: string | null
  contactCount: number
  verificationStatusCode: VerificationStatusCode
  verifiedAt?: string | null
  doNotContact: boolean
  doNotContactReasonCode?: SuppressionReasonCode | null
  attributes?: Record<string, unknown> | null
  sourceCode: string
  statusCode: VoterStatusCode
  version: number
  createdAt: string
  updatedAt?: string | null
}

/**
 * voter/dto/VoterPiiResponse — its own endpoint, its own permission, audited
 * server-side once per call. Never fetched to render a list or a drawer.
 */
export interface VoterPiiResponse {
  voterId: string
  nationalId?: string | null
  channels: {
    id: string
    kindCode: ChannelKindCode
    value: string
    primary: boolean
    verified: boolean
    statusCode: string
    verifiedAt?: string | null
  }[]
}

export interface VoterStatisticsResponse {
  total: number
  bySupportBand: Record<string, number>
  byAgeBand: Record<string, number>
  byVerificationStatus: Record<string, number>
  contactable: number
  suppressed: number
}

// ── the dossier (shared.dossier) ────────────────────────────────────────────

export type SectionStatus = 'OK' | 'EMPTY' | 'FORBIDDEN' | 'ERROR' | 'TIMEOUT'

export interface DossierSection {
  sectionCode: string
  title: string
  order: number
  status: SectionStatus
  rows: Record<string, unknown>[]
  summary?: Record<string, unknown> | null
  message?: string | null
}

export interface PendingSection {
  sectionCode: string
  title: string
  availableFromPhase: number
}

/** voter/dto/VoterDossierResponse. */
export interface VoterDossierResponse {
  voterId: string
  displayName: string
  sections: DossierSection[]
  availableSections: string[]
  pendingSections: PendingSection[]
  assembledAt: string
}

// ── voter · entity-shaped responses (see the header note) ───────────────────

interface BaseEntityFields {
  id: string
  version: number
  createdAt: string
  updatedAt?: string | null
  createdBy?: string | null
  updatedBy?: string | null
}

export interface VoterContactEntity extends BaseEntityFields {
  voterId: string
  channel: ContactChannelCode
  direction: 'OUTBOUND' | 'INBOUND'
  outcome: ContactOutcomeCode
  occurredAt: string
  durationSeconds?: number | null
  mobilizerId?: string | null
  recordedBy?: string | null
  sentiment?: SentimentCode | null
  supportBefore?: number | null
  supportAfter?: number | null
  topics?: string[] | null
  notes?: string | null
  followUpAt?: string | null
  unitPath?: string | null
}

export interface VoterConsentEntity extends BaseEntityFields {
  voterId: string
  purpose: ConsentPurposeCode
  granted: boolean
  method: ConsentMethodCode
  evidenceFileId?: string | null
  capturedBy?: string | null
  capturedAt: string
  capturedIp?: string | null
  capturedDevice?: string | null
  witnessName?: string | null
  expiresAt?: string | null
  supersedesId?: string | null
  notes?: string | null
}

export interface VoterVerificationEntity extends BaseEntityFields {
  voterId: string
  method: string
  result: 'PASSED' | 'FAILED' | 'INCONCLUSIVE'
  evidenceFileId?: string | null
  verifiedBy?: string | null
  verifiedAt: string
  notes?: string | null
}

export interface VoterTurnoutRecordEntity extends BaseEntityFields {
  voterId: string
  electionYear: number
  electionType: string
  voted?: boolean | null
  votedAt?: string | null
  stationId?: string | null
  evidence?: string | null
  recordedBy?: string | null
  recordedAt?: string | null
}

/**
 * ── voter tags (prompt 40) ─────────────────────────────────────────────────
 *
 * `tag` is the CANONICAL form and `displayName` is what a human reads. The two
 * are not interchangeable and the distinction is load-bearing: `voter_tag.tag`
 * is a plain `varchar`, deliberately not `citext`, so every comparison against
 * it is case-sensitive. The targeting presets shipped asking for `"BODA"` while
 * the writer stored `boda`, and all six tag-based presets matched nobody for as
 * long as they existed.
 *
 * So: a picker offers `displayName` and sends `tag`. Nothing in this app ever
 * sends a string a human typed straight into a filter or a rule.
 */
export interface VoterTagResponse {
  tag: string
  /** From the vocabulary, so a row renders "Boda boda riders" without a second call. */
  displayName: string
  appliedBy?: string | null
  appliedAt: string
  source?: string | null
}

export interface TagVocabularyResponse {
  tag: string
  displayName: string
  category?: string | null
  description?: string | null
  /** Hidden from the picker. Still matched by presets and segments — see the manager's copy. */
  active: boolean
  /** Computed from a GROUP BY, never a stored counter. */
  voterCount: number
}

/** Unpaginated by design, with the assumption made visible rather than assumed. */
export interface TagVocabularyListResponse {
  tags: TagVocabularyResponse[]
  truncated: boolean
  cap: number
}

/**
 * What a merge did — and what it deliberately did NOT do. A merge rewrites no
 * targeting rule, so these two lists are the consequence the caller has to be
 * told about.
 */
export interface TagMergeResult {
  from: string
  into: string
  moved: number
  alreadyPresent: number
  affectedPresetKeys: string[]
  affectedSegmentIds: string[]
}

/** `requested === applied + alreadyPresent + outOfScope + notFound`, always. */
export interface BulkTagResult {
  tag: string
  requested: number
  applied: number
  alreadyPresent: number
  outOfScope: number
  notFound: number
}

export interface VoterPlacementHistoryEntity extends BaseEntityFields {
  voterId: string
  fromStationId?: string | null
  toStationId?: string | null
  fromUnitPath?: string | null
  toUnitPath?: string | null
  reason: string
  transferRequestId?: string | null
  reversalOfId?: string | null
  changedBy?: string | null
  changedAt: string
  notes?: string | null
}

export interface VoterHouseholdEntity extends BaseEntityFields {
  code: string
  pollingStationId: string
  unitPath?: string | null
  headVoterId?: string | null
  name?: string | null
  addressDescription?: string | null
  memberCount: number
  dwellingType?: string | null
  notes?: string | null
}

export interface VoterSegmentEntity extends BaseEntityFields {
  code: string
  name: string
  description?: string | null
  definition: Record<string, unknown>
  scopeUnitId?: string | null
  scopeUnitPath?: string | null
  refreshMode: 'MANUAL' | 'NIGHTLY' | 'ON_CHANGE'
  memberCount: number
  lastRefreshedAt?: string | null
  lastRefreshMs?: number | null
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
}

export interface SegmentPreviewResponse {
  count: number
  sample: VoterResponse[]
}

// ── integrations ────────────────────────────────────────────────────────────

export type IntegrationCategoryCode =
  'SOCIAL' | 'NEWS' | 'LEGAL' | 'REGISTRY' | 'PAYMENT' | 'MESSAGING' | 'IDENTITY'
  | 'MAPPING' | 'ANALYTICS' | 'AI' | 'STORAGE' | 'OTHER'
export type IntegrationStatusCode =
  'DRAFT' | 'ACTIVE' | 'DISABLED' | 'RATE_LIMITED' | 'ERROR' | 'MAINTENANCE'
export type HealthCode = 'HEALTHY' | 'DEGRADED' | 'DOWN' | 'UNKNOWN'
export type AuthKindCode =
  'NONE' | 'API_KEY' | 'BEARER' | 'BASIC' | 'HMAC'
  | 'OAUTH2_CLIENT_CREDENTIALS' | 'OAUTH2_AUTH_CODE' | 'MTLS'
export type CredentialStatusCode = 'ACTIVE' | 'ROTATING' | 'RETIRED' | 'REVOKED' | 'EXPIRED'

export interface IntegrationResponse {
  id: string
  code: string
  environment: 'SANDBOX' | 'PRODUCTION'
  displayName: string
  category: IntegrationCategoryCode
  provider: string
  authKind: AuthKindCode
  capabilities: string[]
  status: IntegrationStatusCode
  health: HealthCode
  consecutiveFailures: number
  quotaUsedToday: number
  requestsToday: number
  baseUrl?: string | null
  config: Record<string, unknown>
  rateLimit: Record<string, unknown>
  scopeUnitId?: string | null
  notes?: string | null
  /**
   * The optimistic-lock version, echoed back as `If-Match` on PATCH.
   *
   * Optional because a backend before prompt 26 §2 does not send it, and its
   * absence is exactly what tells the UI that editing is impossible on that
   * build — `PATCH` requires the header, so with no version there is nothing to
   * put in it and a Save button could only ever produce a 400. See
   * `features/integrations/EditRegistration.tsx`.
   */
  version?: number
}

/**
 * integration/dto/CredentialResponse.
 *
 * There is no `value` field and there is no endpoint that would populate one —
 * see integration/implementation_plan.md §3.2 rule 2. Do not add a reveal
 * affordance to any component that consumes this type; there is nothing behind it.
 */
export interface CredentialResponse {
  id: string
  credentialKind: string
  versionNo: number
  fingerprint: string
  status: CredentialStatusCode
  issuedAt: string
  expiresAt?: string | null
  lastUsedAt?: string | null
  useCount: number
}

export interface ProviderResponse {
  provider: string
  authKind: AuthKindCode
  capabilities: string[]
  configKeys: string[]
  requiredCredentials: string[]
  optionalCredentials: string[]
  rateLimits: Record<string, unknown>
  docsUrl?: string | null
}

export interface HealthCheckResponse {
  ok: boolean
  latencyMs?: number | null
  statusCode?: number | null
  detail?: string | null
}

export interface UsageEntry {
  capability: string
  day: string
  requests: number
  errors: number
  rateLimited: number
  quotaConsumed: number
}

export interface UsageResponse { content: UsageEntry[] }

export interface InboundEventResponse {
  id: string
  integrationId: string
  eventType?: string | null
  externalId?: string | null
  signatureStatus: 'VALID' | 'INVALID' | 'ABSENT' | 'UNSUPPORTED'
  status: 'RECEIVED' | 'DISPATCHED' | 'PROCESSED' | 'FAILED' | 'IGNORED' | 'REPLAYED'
  receivedAt: string
  dispatchedAt?: string | null
  processedAt?: string | null
  attempts: number
  errorDetail?: string | null
  /** Only populated for `integration:admin`. */
  rawBodyBase64?: string | null
}

export interface WebhookEndpointResponse {
  id: string
  name: string
  url: string
  description?: string | null
  eventTypes: string[]
  status: 'ACTIVE' | 'PAUSED' | 'DISABLED'
  maxAttempts: number
  lastSuccessAt?: string | null
  lastFailureAt?: string | null
  /** Prompt 24 §6.1 — distinct from lastSuccessAt: "we tried and got a 502". */
  lastTriggeredAt?: string | null
  lastResponseStatus?: number | null
  consecutiveFailures: number
}

export interface DeliveryResponse {
  id: string
  endpointId: string
  eventType: string
  eventId: string
  attempt: number
  status: 'PENDING' | 'DELIVERED' | 'FAILED' | 'EXHAUSTED'
  responseStatus?: number | null
  responseBody?: string | null
  nextAttemptAt?: string | null
  deliveredAt?: string | null
}

/**
 * A listed or read key. The first eight fields are the original list shape; the
 * rest were added by prompt 24 §5.1 so one record serves both list and detail.
 *
 * `lastUsedAt`, `lastUsedIp` and `useCount` are written by the api-key call
 * interceptor. On a backend that predates it they are null and zero — that is a
 * missing writer, not a key that has never been used, and the detail view says
 * so rather than implying the key is idle.
 */
export interface ApiKeyResponse {
  id: string
  name: string
  prefix: string
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED' | 'EXPIRED'
  scopeLevel: string
  expiresAt: string
  lastUsedAt?: string | null
  useCount: number
  description?: string | null
  authorities?: string[] | null
  scopeUnitId?: string | null
  scopeUnitName?: string | null
  scopeUnitPath?: string | null
  rateLimitPerMinute?: number
  ipAllowlist?: string[] | null
  lastUsedIp?: string | null
  createdBy?: string | null
  revokedBy?: string | null
  revokedAt?: string | null
  revokedReason?: string | null
}

/**
 * The ONLY response in the whole API that carries secret material, once, at
 * creation. `key` is never retrievable again — the UI must say so at the moment
 * it is shown, because there is no second chance and no support path.
 */
export interface CreateApiKeyResponse {
  id: string
  name: string
  key: string
  prefix: string
  scopeLevel: string
  expiresAt: string
}

// ── voter import / export / merge (prompt 18) ───────────────────────────────
//
// These endpoints serialise JPA entities rather than DTOs, so the shapes below
// carry BaseEntity's fields. Prompt 22 §7 lists replacing them with DTOs as
// outstanding; modelling them as `*Entity` keeps the difference visible.

export type ImportBatchStatusCode =
  'PENDING' | 'VALIDATING' | 'VALIDATED' | 'APPLYING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'

export interface VoterImportBatchEntity {
  id: string
  sourceFileId: string
  status: ImportBatchStatusCode
  dryRun: boolean
  mapping: Record<string, unknown>
  defaults?: Record<string, unknown> | null
  totalRows?: number | null
  processedRows: number
  createdCount: number
  updatedCount: number
  unchangedCount: number
  duplicateCount: number
  rejectedCount: number
  lastChunkIndex: number
  report?: Record<string, unknown> | null
  errorFileId?: string | null
  error?: string | null
  attestationRef?: string | null
  scopeUnitPath?: string | null
  validatedAt?: string | null
  startedAt?: string | null
  finishedAt?: string | null
  version: number
  createdAt: string
  updatedAt?: string | null
}

export type ExportJobStatusCode = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'EXPIRED'

export interface VoterExportJobEntity {
  id: string
  filter: Record<string, unknown>
  columns: string[]
  status: ExportJobStatusCode
  rowCount?: number | null
  resultFileId?: string | null
  expiresAt?: string | null
  requestedBy?: string | null
  version: number
  createdAt: string
  updatedAt?: string | null
}

export type DuplicateReviewStatusCode = 'PENDING' | 'MERGED' | 'DISMISSED' | 'DEFERRED'

export interface VoterDuplicateCandidateEntity {
  id: string
  voterAId: string
  voterBId: string
  score: number
  signals: Record<string, unknown>
  reviewStatus: DuplicateReviewStatusCode
  dismissedAt?: string | null
  dismissedBy?: string | null
  version: number
  createdAt: string
  updatedAt?: string | null
}

/** `POST /voters/{id}/merge` returns a preview map when `confirm` is false. */
export interface MergePreview {
  [key: string]: unknown
}

// ─────────────────────────────────────────────────────────────────────────────
// Integration telemetry and reporting (prompt 24 §2, §3, §5.3, §6.5)
//
// Read off the shipped Java records. There are no Jackson annotations anywhere
// in `integration/dto`, so the JSON keys are the record component names verbatim
// and nulls are present rather than omitted.
//
// ⚠️ The three report shapes are NOT interchangeable, and as of prompt 25 §8.10
// they no longer pretend to be. `ApiKeyReportResponse` used to reuse
// `ReportTotals` while filling `quotaUsedToday` with the key's lifetime use
// count and `quotaLimit` with its per-minute rate limit — same names, different
// meanings. It now has its own `ApiKeyTotals` with `useCountLifetime` and
// `rateLimitPerMinute`, which is the honest shape. `WebhookReportResponse` uses
// different names again. The web therefore renders each with its own adapter
// into a common view model rather than pointing one component at all three; see
// `report.ts`. They stay separate — the shapes are genuinely different subjects,
// so a single shared type would be the lie all over again.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReportTotals {
  requests: number
  errors: number
  rateLimited: number
  successRate: number
  p50LatencyMs?: number | null
  p95LatencyMs?: number | null
  quotaUsedToday: number
  quotaLimit: number
}

/** `bucket` is `YYYY-MM-DD`, or `YYYY-MM-DDTHH` when `bucket=HOUR`. */
export interface ReportSeriesPoint {
  bucket: string
  requests: number
  errors: number
  p95LatencyMs?: number | null
}

export interface CapabilityCount { capability: string; requests: number; errors: number }
export interface PathCount { path: string; requests: number; errors: number }
export interface OutcomeCount { outcome: string; count: number }
/**
 * ⚠️ The component is `dimensionValue`, not `value`. It was renamed by prompt 25
 * §9.3: `NoPlaintextEndpointTest` flags any `String` component named `value`
 * reachable from a controller return type, and a facet label ("MALE", "citizen")
 * is not a secret, so the field was renamed rather than added to the test's
 * exception list. The name here must track the Java record exactly — Jackson is
 * unannotated, so a mismatch is a silently `undefined` chart label, not an error.
 */
export interface DimensionCount { dimensionValue: string; count: number }

export interface IntegrationReportResponse {
  totals: ReportTotals
  series: ReportSeriesPoint[]
  byCapability: CapabilityCount[]
  byOutcome: OutcomeCount[]
  /** Keyed by the dimension name the provider declared, e.g. `gender`. */
  dimensions: Record<string, DimensionCount[]>
}

/**
 * A key's own totals. Deliberately NOT `ReportTotals`: a key has no daily quota,
 * it has a lifetime call count and a per-minute ceiling, and those are different
 * questions from "how much of today's allowance is spent".
 */
export interface ApiKeyTotals {
  requests: number
  errors: number
  rateLimited: number
  successRate: number
  p50LatencyMs?: number | null
  p95LatencyMs?: number | null
  /** Every call ever made with this key, counted by the interceptor. */
  useCountLifetime: number
  /** The ceiling, not a consumption figure. */
  rateLimitPerMinute: number
}

export interface ApiKeyReportResponse {
  totals: ApiKeyTotals
  series: ReportSeriesPoint[]
  byPath: PathCount[]
  byOutcome: OutcomeCount[]
}

export interface WebhookReportResponse {
  totals: { attempted: number; succeeded: number; failed: number; p95LatencyMs?: number | null }
  series: { bucket: string; attempted: number; succeeded: number; failed: number; p95LatencyMs?: number | null }[]
  byStatus: { status: string; count: number }[]
  byEventType: { eventType: string; count: number }[]
}

export type CallOutcome =
  | 'SUCCESS' | 'NOT_FOUND' | 'CLIENT_ERROR' | 'SERVER_ERROR' | 'TIMEOUT'
  | 'RATE_LIMITED' | 'CIRCUIT_OPEN' | 'TRANSPORT_ERROR' | '__UNKNOWN__'

/** One recorded call. Carries no subject value, no URL and no query map. */
export interface IntegrationCallResponse {
  id: string
  capability: string
  direction: 'OUTBOUND' | 'INBOUND' | '__UNKNOWN__'
  outcome: CallOutcome
  /** null means the call never reached the wire — a transport failure, not a 4xx. */
  httpStatus?: number | null
  latencyMs: number
  attempt: number
  requestId?: string | null
  upstreamRef?: string | null
  errorCode?: string | null
  /**
   * The provider's own explanation of a failure, or null.
   *
   * Explanation fields only — never the raw response body, which routinely echoes the request
   * that caused the error and would put subject values into the telemetry table.
   */
  errorMessage?: string | null
  dimensions?: Record<string, string> | null
  calledAt: string
}

export interface WebhookEventTypeResponse {
  code: string
  displayName: string
  description?: string | null
}

/** `POST /webhooks/{id}/rotate-secret` — shown once, never retrievable. */
export interface RotateSecretResponse { secret: string }

/** `POST /integrations/{id}/credentials/test` — a local decrypt check only. */
export interface LocalDecryptResponse { decryptable: boolean; resolved: number }

// ─────────────────────────────────────────────────────────────────────────────
// Voter identity lookup (prompt 24 §7.8)
//
// ⚠️ Contract change: `POST /voters/lookup` used to return 404 on a miss. It now
// always returns 200 with `found: false`. Any client branching on the status
// code is wrong.
// ─────────────────────────────────────────────────────────────────────────────

export interface VoterLookupPrefill {
  firstName?: string | null
  /** eCitizen's `last_name` is the MIDDLE name; `surname` is the family name. */
  middleName?: string | null
  surname?: string | null
  /** Year only — the full date of birth never leaves the backend adapter. */
  birthYear?: number | null
  genderCode?: 'MALE' | 'FEMALE' | null
  citizenship?: string | null
  email?: string | null
  mobileNumber?: string | null
  mobileVerified: boolean
}

export interface VoterLookupResponse {
  found: boolean
  alreadyRegistered: boolean
  existingVoterId?: string | null
  source: 'LOCAL' | 'ECITIZEN_IPRS' | 'NONE'
  /** Non-null only when `source === 'ECITIZEN_IPRS'`. */
  prefill?: VoterLookupPrefill | null
  /** eCitizen's own `verified` flag — the state's assertion, not ours. */
  upstreamVerified: boolean
  retrievedAt?: string | null
  upstreamRef?: string | null
  /**
   * Who this is and where they are, in the SAME call (prompt 28 B6). All six are
   * null unless the register knows this person (`alreadyRegistered`). The unit
   * names are resolved server-side, because a browser walking an ltree to turn
   * four ids into four names is four more round trips on the one connection that
   * cannot afford them.
   *
   * These were missing from this type until the beneficiary enrol dialog needed
   * `displayName` to say WHO it had found.
   */
  displayName?: string | null
  registrationStatusCode?: string | null
  countyName?: string | null
  constituencyName?: string | null
  wardName?: string | null
  pollingStationName?: string | null
}

/** Captured with a new voter (prompt 24 §7.7). Sealed and blind-indexed server-side. */
export interface ContactChannelRequest {
  kindCode: 'PHONE' | 'ALT_PHONE' | 'WHATSAPP' | 'EMAIL' | 'USSD_MSISDN'
  value: string
  primary: boolean
  verified: boolean
}

// ─────────────────────────────────────────────────────────────────────────────
// Relocation — the voter transfer workflow (prompt 27, Phase 3)
//
// Read off `backend/openapi.json` after the feature shipped, not off the plan.
// Jackson is unannotated here as everywhere, so a JSON key IS the Java record
// component name and a rename lands as a silent `undefined`.
//
// ⚠️ The vocabulary is load-bearing and legally so. This system does NOT transfer
// voters — under Kenyan law that is an IEBC process. It records that a transfer
// was requested, consented to, checked, approved, lodged with the IEBC and
// confirmed, and only then updates OUR record of where the voter is registered.
// `RECORDED` means "we recorded that this happened", never "we did this". The
// backend has a `TerminologyTest` that fails the build on active-voice strings;
// the web has no such test, so it is on the reader of this file.
// ─────────────────────────────────────────────────────────────────────────────

export type TransferStatusCode =
  | 'DRAFT' | 'SUBMITTED' | 'VERIFYING' | 'INELIGIBLE' | 'UNDER_REVIEW'
  | 'APPROVED' | 'REJECTED' | 'WITHDRAWN' | 'EXPIRED' | 'UNDER_APPEAL'
  | 'LODGED' | 'CONFIRMED' | 'FAILED_AT_IEBC' | 'RECORDED' | 'REVERSED'
  | '__UNKNOWN__'

export type EligibilityResultCode = 'PASS' | 'FAIL' | 'WAIVED' | 'SKIPPED' | '__UNKNOWN__'

/** One request in a list. `version` is the If-Match value for PATCH. */
/**
 * TRANSFER = correct where an existing voter is registered.
 * REGISTRATION = assist an eligible citizen who is not on the roll at all.
 * The two share one workflow, one queue and one evidence trail; they differ in their
 * gate set, in whether there is an origin station, and in what RECORD writes.
 */
export type RequestKindCode = 'TRANSFER' | 'REGISTRATION'

export interface TransferRequestResponse {
  id: string
  reference: string
  /** Absent on a backend older than prompt 28 — treat undefined as TRANSFER. */
  kind?: RequestKindCode
  voterId: string
  /**
   * Identity, resolved server-side through the voter port. Every one of these is
   * optional: a voter that has been erased, merged away or is outside the caller's
   * scope resolves to nothing, and the request must still appear in the queue —
   * a row that vanishes because its subject was erased is an audit hole.
   *
   * `nationalIdMasked` is the LAST FOUR DIGITS ONLY, masked inside the voter feature.
   * The full number never crosses the boundary and never reaches the browser.
   */
  voterName?: string | null
  voterNumber?: string | null
  nationalIdMasked?: string | null
  voterRegistrationStatus?: string | null
  fromStationName?: string | null
  toStationName?: string | null
  /** Null on a REGISTRATION — there is no origin station, which is the point of it. */
  fromStationId: string | null
  toStationId: string
  /** Null on a REGISTRATION. */
  fromUnitPath: string | null
  toUnitPath: string
  intakeChannel: string
  reason: string
  status: TransferStatusCode
  substatus?: string | null
  priority: string
  assignedToUserId?: string | null
  batchId?: string | null
  appealOfId?: string | null
  iebcReference?: string | null
  slaDueAt?: string | null
  /** Computed server-side — do not re-derive it from slaDueAt in the browser. */
  slaBreached: boolean
  escalationLevel: number
  expiresAt?: string | null
  recordedAt?: string | null
  placementHistoryId?: string | null
  createdAt: string
  /** When the status last changed — what the queue-age chart is built from. */
  statusChangedAt?: string | null
  version: number
}

/** One gate's verdict. `problemType` is the URI the API would refuse with. */
export interface GateResult {
  gate: string
  result: EligibilityResultCode
  detail?: string | null
  problemType?: string | null
  waivedBy?: string | null
  waiverReason?: string | null
  checkedAt: string
}

export interface EligibilityReport {
  requestId: string
  status: TransferStatusCode
  kind?: RequestKindCode
  eligible: boolean
  /**
   * Ordered, and filtered to the gates that apply to this request's kind. The UI
   * renders THIS, in THIS order — it does not hold its own list of gates, because a
   * gate the server stopped running would otherwise keep rendering as "not checked".
   */
  gates: GateResult[]
}

/** What gates exist and which kind each applies to. Labels are the web's. */
export interface GateCatalogueEntry {
  gate: string
  appliesTo: RequestKindCode[]
  dangerous?: boolean
}

export interface ConsentResponse {
  id: string
  requestId: string
  voterConsentId?: string | null
  method: string
  evidenceFileId?: string | null
  otpReference?: string | null
  witnessName?: string | null
  witnessPhone?: string | null
  witnessIdRef?: string | null
  capturedBy: string
  capturedAt: string
  capturedDevice?: string | null
  latitude?: number | null
  longitude?: number | null
  /** The capturer's statement, verbatim. Never truncated for display. */
  attestation: string
  revokedAt?: string | null
  revokedReason?: string | null
}

export interface DecisionResponse {
  id: string
  decision: string
  fromStatus?: string | null
  toStatus?: string | null
  actorUserId: string
  actorRole?: string | null
  notes?: string | null
  decidedAt: string
}

export interface TransferNoteResponse {
  id: string
  body: string
  internal: boolean
  authorId: string
  createdAt: string
}

export interface TransferDetailResponse {
  request: TransferRequestResponse
  consent?: ConsentResponse | null
  gates: GateResult[]
  decisions: DecisionResponse[]
  notes: TransferNoteResponse[]
  /**
   * Which decisions are legal from the current state, decided by the backend's
   * `TransferStateMachine`.
   *
   * The UI renders its action buttons from THIS and never from its own reading
   * of the status. Reimplementing a fifteen-state machine in the browser would
   * guarantee the two drift, and the failure mode is offering an operator a
   * button that 409s — or worse, hiding the one action they need.
   */
  allowedDecisions: string[]
}

export interface TransferBatchResponse {
  id: string
  reference: string
  title: string
  kind: string
  sourceUnitId?: string | null
  targetUnitId?: string | null
  scopeUnitPath: string
  status: string
  parameters?: Record<string, unknown> | null
  requestCount: number
  approvedCount: number
  recordedCount: number
  rejectedCount: number
  generatedBy?: string | null
  generatedAt?: string | null
  closedBy?: string | null
  closedAt?: string | null
  version: number
}

export interface BatchActionReport {
  batchId: string
  attempted: number
  succeeded: number
  failed: number
  outcomes: { requestId: string; ok: boolean; detail?: string | null }[]
}

export interface DayCount {
  date: string
  count: number
}

export interface TransferStatisticsResponse {
  total: number
  byStatus: Record<string, number>
  byReason: Record<string, number>
  byChannel: Record<string, number>
  /** Prompt 28. Absent on an older backend — the charts that need it hide themselves. */
  byKind?: Record<string, number>
  /** Zero-filled across the whole range: a sparse series draws a chart that lies. */
  raisedDaily?: DayCount[]
  recordedDaily?: DayCount[]
  /** Open requests bucketed by days in their CURRENT status: "0-3" | "4-7" | "8-14" | "15+". */
  openAgeBuckets?: Record<string, number>
  slaBreached: number
  scopePath?: string | null
}

/**
 * Transfer analysis — everything the Analysis screen draws, in one call.
 *
 * One call rather than six because every panel shares the same filters and the same
 * date range, and six endpoints would drift the moment one of them forgot a filter.
 * Server-computed for the obvious reason: the numbers are over the whole scope, and
 * paging fifty thousand requests into a browser to count them is not analysis.
 */
export interface TransferAnalysisResponse {
  /** Echoed back so the screen can say what it is showing, in the server's own words. */
  from: string
  to: string
  scopePath?: string | null

  raised: number
  recorded: number
  rejected: number
  open: number
  /** Recorded ÷ resolved. Null when nothing has resolved yet — never 0, which would read as failure. */
  successRate?: number | null
  /** Median days from raised to recorded, over requests recorded IN the range. */
  medianDaysToRecord?: number | null
  /** The same numbers for the preceding window of equal length, for a delta. */
  previous?: { raised: number; recorded: number; successRate?: number | null } | null

  /** Lifecycle survival: raised → submitted → approved → lodged → recorded. Ordered. */
  funnel: { stage: string; count: number }[]
  /** How resolved requests ended. */
  outcomes: Record<string, number>

  /**
   * Per-station movement. `recorded` is what the station GAINED, `lost` what it gave up
   * — the two questions a campaign asks about a station are different questions, and a
   * single "transfers" number answers neither.
   */
  byStation: {
    stationId: string
    stationName: string
    unitPath?: string | null
    recorded: number
    lost: number
    open: number
    net: number
  }[]
  /** The same, rolled up. `level` is COUNTY | CONSTITUENCY | WARD. */
  byUnit: { unitId: string; unitName: string; level: string; recorded: number; lost: number; net: number }[]

  byReason: Record<string, number>
  byChannel: Record<string, number>
  byKind: Record<string, number>
  raisedDaily: DayCount[]
  recordedDaily: DayCount[]
}

/**
 * The field screen, in one call: who this ID belongs to, where they are registered
 * right now, and whether anything is already open for them.
 *
 * Mirrors the backend's `CitizenLookupResponse` (`POST /transfers/lookup`) field for
 * field — including the flat `…Name` columns rather than a nested placement object,
 * because the JSON key IS the Java record component name and inventing a nicer shape
 * here is how a client drifts from its server.
 *
 * `upstream` is the ONE field the server does not send. `/transfers/lookup` answers
 * "does our register know this person"; when it does not, the client asks
 * `POST /voters/lookup`, which is the endpoint that reaches the government identity
 * service, and hangs the answer here. Two calls, and only when the first says no.
 */
export interface TransferLookupResponse {
  /** Known to the voter register — registered, or recorded but not yet on the roll. */
  found: boolean
  voterId?: string | null
  displayName?: string | null
  voterNumber?: string | null
  /** Last four digits only, masked inside the voter feature. */
  nationalIdMasked?: string | null
  registrationStatusCode?: string | null
  pollingStationId?: string | null
  countyName?: string | null
  constituencyName?: string | null
  wardName?: string | null
  pollingStationName?: string | null
  /** A request already open for this person — a second one is refused. */
  openTransferRequestId?: string | null
  openTransferReference?: string | null
  openTransferStatus?: string | null
  openTransferKind?: string | null
  /**
   * Client-assembled, never server-sent: what the identity service returned when the
   * register had nothing. Shown as a proposal, never applied silently.
   */
  upstream?: {
    found: boolean
    source: string
    firstName?: string | null
    middleName?: string | null
    surname?: string | null
    birthYear?: number | null
    genderCode?: string | null
    verified: boolean
  } | null
}

// ═══════════════════════════════════════════════════════════════════════════
// Phase 4 — mobilizer (the field network)
//
// Mirrors `ke.zurimate.axis.mobilizer.dto.*` exactly. Jackson 3 is unannotated
// here, so the JSON key IS the Java record component name: `maskedPhone`, not
// `masked_phone`, and `roleCode`, not `role`. Do not tidy these.
// ═══════════════════════════════════════════════════════════════════════════

export type MobilizerRoleCode =
  | 'NATIONAL_DIRECTOR' | 'NATIONAL_COORDINATOR' | 'COUNTY_COORDINATOR'
  | 'CONSTITUENCY_CAPTAIN' | 'WARD_CAPTAIN' | 'MOBILIZER' | 'CHIEF_AGENT'
  | 'POLLING_AGENT' | 'PARTY_AGENT' | 'WATCHER' | 'UNKNOWN'

export type MobilizerStatusCode =
  | 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'TERMINATED' | 'UNKNOWN'

export type AdoptionStatusCode = 'ACTIVE' | 'RELEASED' | 'SUPERSEDED' | 'UNKNOWN'

/** Mirrors ke.zurimate.axis.mobilizer.domain.ActivityType — all 24, not the first 15. */
export type ActivityTypeCode =
  | 'CHECK_IN' | 'CHECK_OUT' | 'VOTER_REGISTERED' | 'VOTER_VERIFIED'
  | 'VOTER_CONTACTED' | 'SURVEY_COMPLETED' | 'MOOD_REPORTED' | 'INCIDENT_REPORTED'
  | 'PROGRAM_ENROLLED' | 'PROGRAM_DELIVERED' | 'TRANSFER_RAISED' | 'TRANSFER_RECORDED'
  | 'CALL_MADE' | 'DOOR_TO_DOOR' | 'MEETING_ATTENDED' | 'NOTE_ADDED'
  | 'FIELD_QUALITY_FLAGGED' | 'CELL_FORMED' | 'CELL_MEMBER_ENROLLED' | 'PLEDGE_CAPTURED'
  | 'TURNOUT_CHECKIN' | 'QUEUE_REPORTED' | 'INCENTIVE_DELIVERED' | 'ESCALATION_RESOLVED'
  | 'UNKNOWN'

export type ActivitySourceCode = 'FIELD' | 'SYSTEM' | 'IMPORT' | 'UNKNOWN'
export type ActivitySubjectCode = 'VOTER' | 'TRANSFER' | 'STATION' | 'TASK' | 'UNKNOWN'

/** Computed per request, never stored — so there is no `UNKNOWN` member. */
export type CoverageStatusCode = 'GAP' | 'UNDERSTAFFED' | 'OK' | 'OVERSTAFFED'

export type MissionKindCode =
  | 'ADOPT_STATION' | 'VERIFY_VOTERS' | 'CONTACT_VOTERS' | 'CHECK_IN'
  | 'SURVEY_COLLECTION' | 'PROGRAM_ENROLMENT' | 'UNKNOWN'

export type MissionStatusCode =
  | 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'UNKNOWN'

export type TaskStatusCode =
  | 'ASSIGNED' | 'ACCEPTED' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED'
  | 'CANCELLED' | 'UNKNOWN'

export type TaskPriorityCode = 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN'

export type TierCode = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM' | 'DIAMOND' | 'UNKNOWN'

export type BadgeCode =
  | 'TOP_RATED' | 'CLOSER' | 'HIGH_PERFORMER' | 'ACTIVITY_BEAST' | 'DATA_HUNGRY'
  | 'STREAK_MASTER' | 'VOTER_WHIRLWIND' | 'PEOPLE_LEADER' | 'SURVEY_PRO'
  | 'MOMENTUM' | 'UNKNOWN'

// ── the person ──────────────────────────────────────────────────────────────

export interface UnitRef {
  id: string
  name?: string | null
  typeCode?: string | null
  path?: string | null
  primary: boolean
}

export interface MobilizerRefResponse {
  id: string
  fullName: string
  roleCode: MobilizerRoleCode
  statusCode: MobilizerStatusCode
}

/**
 * The roster row. `maskedPhone` is the ONLY phone this type carries — the full
 * number exists solely on `MobilizerContactResponse`, behind an audited call.
 */
export interface MobilizerResponse {
  id: string
  fullName: string
  maskedPhone?: string | null
  email?: string | null
  roleCode: MobilizerRoleCode
  statusCode: MobilizerStatusCode
  primaryUnitId?: string | null
  primaryUnitName?: string | null
  primaryUnitPath?: string | null
  assignmentCount: number
  supervisorId?: string | null
  supervisorName?: string | null
  subordinateCount: number
  adoptionCount: number
  userId?: string | null
  lastActivityAt?: string | null
  createdAt?: string | null
  version: number
}

export interface MobilizerDetailResponse {
  profile: MobilizerResponse
  assignments: UnitRef[]
  /** Nearest supervisor first, up to the root. */
  supervisorChain: MobilizerRefResponse[]
  joinedOn?: string | null
  notes?: string | null
  terminationReason?: string | null
}

/** Audited on every call. Never cache it, never put it in a URL. */
export interface MobilizerContactResponse {
  id: string
  fullName: string
  phone?: string | null
  nationalId?: string | null
  email?: string | null
}

export interface MobilizerStatisticsResponse {
  total: number
  active: number
  withUserAccount: number
  byRole: Record<string, number>
  byStatus: Record<string, number>
  byUnit: { unitId: string; unitName?: string | null; unitTypeCode?: string | null; count: number }[]
}

export interface AssignmentResponse {
  mobilizerId: string
  assignments: UnitRef[]
  primaryUnitId?: string | null
  changedAt?: string | null
}

export interface SupervisorMoveResponse {
  mobilizerId: string
  supervisorId?: string | null
  hierarchyPath?: string | null
  /** How many people the subtree move touched — shown in the confirm dialog. */
  moved: number
}

export interface HierarchyNodeResponse {
  id: string
  fullName: string
  roleCode: MobilizerRoleCode
  statusCode: MobilizerStatusCode
  supervisorId?: string | null
  primaryUnitId?: string | null
  primaryUnitName?: string | null
  subordinateCount: number
  hasSubordinates: boolean
}

export interface TerminationImpact {
  mobilizerId: string
  fullName: string
  adoptionsReleased: number
  tasksReturned: number
  subordinatesReparented: number
  newSupervisorId?: string | null
  newSupervisorName?: string | null
  assignmentsKept: number
  subordinates: MobilizerRefResponse[]
  /** True when terminating would orphan subordinates — set a supervisor first. */
  blocked: boolean
  blockReason?: string | null
}

// ── adoption ────────────────────────────────────────────────────────────────

export interface AdoptionResponse {
  id: string
  mobilizerId: string
  mobilizerName?: string | null
  stationId: string
  stationName?: string | null
  stationPath?: string | null
  statusCode: AdoptionStatusCode
  adoptedAt?: string | null
  adoptedBy?: string | null
  releasedAt?: string | null
  releaseReason?: string | null
  supersededBy?: string | null
}

/**
 * One entry per requested station. Partial success is the contract here, not a
 * bug: twenty-nine adopted and one refused because a colleague got there first
 * is a useful outcome, and rolling back all thirty is not.
 */
export interface AdoptionResult {
  stationId: string
  stationName?: string | null
  adopted: boolean
  reasonCode?: string | null
  adoptionId?: string | null
  currentMobilizerId?: string | null
  currentMobilizerName?: string | null
}

export interface AdoptionBatchResponse {
  requested: number
  adopted: number
  refused: number
  results: AdoptionResult[]
}

export interface StationAdoptionResponse {
  stationId: string
  stationName?: string | null
  current?: AdoptionResponse | null
  history: AdoptionResponse[]
}

// ── coverage ────────────────────────────────────────────────────────────────

export interface CoverageTotals {
  stations: number
  stationsAdopted: number
  voters: number
  votersCovered: number
  mobilizers: number
  /** null, never 0, when there are no mobilizers — the ratio is undefined. */
  votersPerMobilizer?: number | null
  byStatus: Record<string, number>
}

export interface CoverageRow {
  unitId: string
  unitName?: string | null
  unitTypeCode?: string | null
  unitPath?: string | null
  stations: number
  stationsAdopted: number
  voters: number
  votersCovered: number
  mobilizers: number
  votersPerMobilizer?: number | null
  status: CoverageStatusCode
  /** Station rows only. A ward has no adopter and inventing one would be a lie. */
  adopterId?: string | null
  adopterName?: string | null
}

export interface CoverageResponse {
  scopePath?: string | null
  scopeName?: string | null
  level: string
  totals: CoverageTotals
  rows: CoverageRow[]
}

// ── activity ────────────────────────────────────────────────────────────────

export interface ActivityResponse {
  id: string
  mobilizerId: string
  mobilizerName?: string | null
  mobilizerMaskedPhone?: string | null
  activityTypeCode: ActivityTypeCode
  occurredAt: string
  recordedAt?: string | null
  unitId?: string | null
  unitName?: string | null
  scopeUnitPath?: string | null
  subjectTypeCode?: ActivitySubjectCode | null
  subjectId?: string | null
  latitude?: string | number | null
  longitude?: string | number | null
  accuracyMetres?: number | null
  sourceCode: ActivitySourceCode
  detail?: Record<string, unknown> | null
}

/**
 * `source` is set FIELD server-side and refused in the body — a client that can
 * name its own source can name SYSTEM and skip the backdate window.
 */
export interface ActivityRequestBody {
  activityType: ActivityTypeCode
  occurredAt: string
  unitId?: string | null
  subjectType?: ActivitySubjectCode | null
  subjectId?: string | null
  latitude?: number | null
  longitude?: number | null
  accuracyMetres?: number | null
  detail?: Record<string, unknown> | null
}

// ── missions and tasks ──────────────────────────────────────────────────────

export interface MissionResponse {
  id: string
  code: string
  title: string
  description?: string | null
  kindCode: MissionKindCode
  parameters?: Record<string, unknown> | null
  scopeUnitId?: string | null
  scopeUnitName?: string | null
  scopeUnitPath?: string | null
  startsOn: string
  endsOn: string
  targetPerTask?: number | null
  priorityCode: TaskPriorityCode
  statusCode: MissionStatusCode
  tasksTotal: number
  tasksCompleted: number
  version: number
}

export interface TaskResponse {
  id: string
  missionId: string
  missionCode?: string | null
  missionTitle?: string | null
  kindCode: MissionKindCode
  mobilizerId: string
  mobilizerName?: string | null
  unitId?: string | null
  unitName?: string | null
  statusCode: TaskStatusCode
  priorityCode: TaskPriorityCode
  targetCount?: number | null
  /** Recomputed nightly from the activity log — never a client-supplied count. */
  progressCount: number
  /** Live count at read time. Present for the task's owner; never written. */
  liveProgressCount?: number | null
  dueOn?: string | null
  assignedAt?: string | null
  acceptedAt?: string | null
  completedAt?: string | null
  outcomeNote?: string | null
  /** What the state machine will accept next. Drive the buttons off this. */
  allowedTransitions: TaskStatusCode[]
  derived: boolean
  version: number
}

export interface TaskAssignResponse {
  missionId: string
  requested: number
  created: number
  alreadyAssigned: number
}

export interface MissionTaskAggregate {
  assigned: number
  accepted: number
  inProgress: number
  completed: number
  expired: number
  cancelled: number
}

// ── scoring ─────────────────────────────────────────────────────────────────

export interface LeaderboardRow {
  rank: number
  /** null, not 0, for a new entrant — they did not "move from zero". */
  previousRank?: number | null
  movement?: number | null
  mobilizerId: string
  fullName: string
  roleCode: MobilizerRoleCode
  primaryUnitId?: string | null
  primaryUnitName?: string | null
  score: string | number
  tier?: TierCode | null
  activities: number
  flagged: number
  votersRegistered: number
  votersVerified: number
  votersContacted: number
  transfersRaised: number
  transfersRecorded: number
  tasksCompleted: number
  streakDays: number
  badges: BadgeCode[]
}

export interface LeaderboardResponse {
  from: string
  to: string
  scopePath?: string | null
  rulesVersion: number
  computedAt?: string | null
  /** The range spans two weightings — the trend line is not comparable. */
  rulesChangedInRange: boolean
  eligible: number
  excluded: number
  rows: LeaderboardRow[]
  tierDistribution: Record<string, number>
}

export interface MobilizerDayCount { day: string; count: number }
export interface ScoreBucket { from: number; to: number; count: number }
export interface UnitPerformance {
  unitId: string
  unitName?: string | null
  unitPath?: string | null
  mobilizers: number
  activities: number
  medianScore?: string | number | null
}
export interface MobilizerPreviousWindow {
  activitiesTotal: number
  medianScore?: string | number | null
  activeMobilizers: number
}

export interface MobilizerAnalyticsResponse {
  from: string
  to: string
  scopePath?: string | null
  rulesVersion: number
  computedAt?: string | null
  rulesChangedInRange: boolean
  activeMobilizers: number
  totalMobilizers: number
  activitiesTotal: number
  activitiesFlagged: number
  /** null, never 0, when nothing has been computed yet. */
  medianScore?: number | null
  meanScore?: number | null
  previous?: MobilizerPreviousWindow | null
  byRole: Record<string, number>
  byTier: Record<string, number>
  byActivityType: Record<string, number>
  byUnit: UnitPerformance[]
  activityDaily: MobilizerDayCount[]
  activeMobilizersDaily: MobilizerDayCount[]
  scoreDistribution: ScoreBucket[]
  tasks: MissionTaskAggregate
}

export interface CompareSeries {
  mobilizerId: string
  fullName: string
  roleCode: MobilizerRoleCode
  score?: string | number | null
  tier?: TierCode | null
  streakDays: number
  activitiesTotal: number
  votersRegistered: number
  votersVerified: number
  votersContacted: number
  transfersRaised: number
  transfersRecorded: number
  tasksCompleted: number
  tasksExpired: number
  daily: MobilizerDayCount[]
}

export interface MobilizerCompareResponse {
  from: string
  to: string
  series: CompareSeries[]
}

export interface PerformanceRecomputeResponse {
  from: string
  to: string
  /** Extended past `to` by window-days − 1: every later window contained `from`. */
  effectiveTo: string
  daysRecomputed: number
  rulesVersion: number
}

// ── the mobilizer's own screen ──────────────────────────────────────────────

export interface ScopeSummary {
  unitId?: string | null
  unitName?: string | null
  unitTypeCode?: string | null
  path?: string | null
  countyName?: string | null
  constituencyName?: string | null
  wardName?: string | null
  stationName?: string | null
}

/** Everything from `score` down is null until the first nightly rollup runs. */
export interface MyStats {
  activitiesToday: number
  activitiesThisWeek: number
  votersRecorded: number
  transfersRaised: number
  transfersRecorded: number
  stationsAdopted: number
  lastCheckInAt?: string | null
  lastCheckInUnitId?: string | null
  lastCheckInUnitName?: string | null
  score?: string | number | null
  tier?: TierCode | null
  streakDays?: number | null
  rank?: number | null
  badges: BadgeCode[]
  flaggedCount: number
  computedAt?: string | null
}

/** `derived: true` items have no id and cannot be accepted — they are a query. */
export interface TaskSummary {
  id?: string | null
  missionId?: string | null
  kindCode: MissionKindCode
  title: string
  statusCode?: TaskStatusCode | null
  priorityCode: TaskPriorityCode
  target?: number | null
  progress: number
  dueOn?: string | null
  dueAt?: string | null
  derived: boolean
}

/** A 200 with a body, never an empty response and never a 403. */
export interface MobilizerMeResponse {
  isMobilizer: boolean
  profile?: MobilizerResponse | null
  assignments: UnitRef[]
  scope?: ScopeSummary | null
  stats?: MyStats | null
  tasks: TaskSummary[]
}

// ════════════════════════════════════════════════════════════════════════════
// PHASE 5 — PROGRAMS
//
// Every response below is what the backend actually returns (prompts 36–39 as
// shipped), and three conventions run through all of it:
//
//  1. A number that needed a voter link, a support reading or a bounded set
//     carries its denominator and its exclusions BESIDE it. `measured` never
//     travels without `measurable`; a de-duplicated total never travels without
//     the sum it de-duplicated.
//  2. A value that cannot be computed is `null` WITH a reason string, never 0.
//     `coverage: null, coverageUnavailableReason: "…"` is the shape.
//  3. Money is per-currency or refused. A figure whose filtered set spans two
//     currencies is `null` with `currencyMixedReason` populated.
//
// The UI's job is to render all three faithfully. A screen that shows a mean
// without its n, or a 0 where the API sent null, is the PollSphere defect this
// whole phase exists to remove.
// ════════════════════════════════════════════════════════════════════════════

export type ProgramCategoryCode =
  | 'BURSARY' | 'HEALTH' | 'WATER' | 'AGRICULTURE' | 'YOUTH_EMPOWERMENT'
  | 'WOMEN_EMPOWERMENT' | 'INFRASTRUCTURE' | 'RELIEF' | 'TRAINING' | 'OTHER' | 'UNKNOWN'

export type ProgramStatusCode =
  | 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED' | 'UNKNOWN'

export type DeliveryChannelCode =
  | 'DIRECT_CASH' | 'MOBILE_MONEY' | 'IN_KIND' | 'VOUCHER' | 'SERVICE' | 'EVENT'
  | 'INSTITUTIONAL' | 'UNKNOWN'

export type BeneficiaryStatusCode =
  | 'SHORTLISTED' | 'ENROLLED' | 'DELIVERED' | 'COMPLETED' | 'DROPPED' | 'UNKNOWN'

export type BeneficiaryEventTypeCode =
  | 'SHORTLISTED' | 'ENROLLED' | 'DELIVERY_RECORDED' | 'COMPLETED' | 'DROPPED'
  | 'REINSTATED' | 'DETAILS_UPDATED' | 'VOTER_LINKED' | 'VOTER_UNLINKED' | 'UNKNOWN'

export type DropReasonCode =
  | 'INELIGIBLE' | 'DUPLICATE' | 'DECLINED' | 'UNREACHABLE' | 'DECEASED'
  | 'MOVED_AWAY' | 'FRAUD_SUSPECTED' | 'PROGRAMME_CANCELLED' | 'OTHER' | 'UNKNOWN'

export type MilestoneStatusCode =
  | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED' | 'CANCELLED' | 'UNKNOWN'

export type MilestonePriorityCode = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'UNKNOWN'

export type KpiDirectionCode =
  | 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER' | 'TARGET_IS_BEST' | 'UNKNOWN'

/** The honesty column. A MANUAL KPI is a number somebody typed. */
export type KpiSourceCode =
  | 'MANUAL' | 'DERIVED_ENROLMENT' | 'DERIVED_DELIVERY' | 'DERIVED_SPEND' | 'UNKNOWN'

export type ProgramDocumentTypeCode =
  | 'PROPOSAL' | 'BUDGET' | 'AGREEMENT' | 'REPORT' | 'PHOTO' | 'RECEIPT'
  | 'ATTENDANCE' | 'OTHER' | 'UNKNOWN'

export type PartnerTypeCode =
  | 'GOVERNMENT' | 'NGO' | 'CBO' | 'FAITH_BASED' | 'PRIVATE_SECTOR' | 'INDIVIDUAL'
  | 'OTHER' | 'UNKNOWN'

export type DisbursementEntryTypeCode = 'DISBURSEMENT' | 'REVERSAL' | 'UNKNOWN'

export type ReversalReasonCode =
  | 'DATA_ENTRY_ERROR' | 'DUPLICATE_PAYMENT' | 'RECIPIENT_INELIGIBLE'
  | 'FRAUD_CONFIRMED' | 'RETURNED_BY_RECIPIENT' | 'PROGRAMME_CANCELLED'
  | 'OTHER' | 'UNKNOWN'

export type SnapshotTriggerCode = 'SCHEDULED' | 'RULE_PUBLISHED' | 'MANUAL' | 'UNKNOWN'

/**
 * Computed per response and stored nowhere — no `UNKNOWN` sentinel, because
 * there is no database string to round-trip.
 */
export type ImpactVerdictCode =
  | 'MEASURED' | 'INSUFFICIENT_DATA' | 'NO_CONTROL' | 'TOO_SOON' | 'NOT_MEASURABLE'

export type EcosystemNodeTypeCode =
  | 'PROGRAMME' | 'MOBILIZER' | 'PARTNER' | 'CHANNEL' | 'UNIT' | 'CANDIDATE' | 'PARTY'

export type EcosystemEdgeTypeCode =
  | 'EXECUTES' | 'FUNDS' | 'DELIVERS_THROUGH' | 'OPERATES_IN' | 'SHARES_BENEFICIARIES'

/**
 * Which question a screen answers. PROGRAMME = "programmes I can see, whole";
 * DELIVERY = "work done in my area". The two give different answers on purpose
 * and no screen may put both in one sentence.
 */
export type ScopingFamilyCode = 'PROGRAMME' | 'DELIVERY'

export type AlertMetricCode =
  | 'COVERAGE' | 'SPEND_PCT_OF_BUDGET' | 'BURN_RATE' | 'DAYS_SINCE_ENROLMENT'
  | 'DROP_RATE' | 'UPLIFT' | 'DUPLICATE_COUNT' | 'RECON_DISCREPANCY'
  | 'MILESTONES_OVERDUE' | 'UNKNOWN'

export type AlertComparatorCode = 'ABOVE' | 'BELOW' | 'UNKNOWN'

export type ProgramExportKindCode =
  | 'PROGRAMMES' | 'BENEFICIARIES' | 'DISBURSEMENTS' | 'OVERLAP' | 'TARGET_GROUPS'
  | 'COVERAGE' | 'GEOGRAPHY' | 'IMPACT' | 'EQUITY' | 'QUALITY' | 'MONEY'
  | 'MOBILIZER_PERFORMANCE' | 'UNKNOWN'

export type ProgramExportStatusCode =
  | 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'EXPIRED' | 'UNKNOWN'

// ── the programme ───────────────────────────────────────────────────────────

export interface ProgramResponse {
  id: string
  code: string
  name: string
  description?: string | null
  categoryCode: ProgramCategoryCode
  statusCode: ProgramStatusCode
  deliveryChannelCode?: DeliveryChannelCode | null
  scopeUnitId?: string | null
  /** ltree. NULL means NATIONAL, and a national programme is visible to everybody. */
  scopeUnitPath?: string | null
  startsOn?: string | null
  endsOn?: string | null
  currency?: string | null
  budgetAmount?: string | number | null
  /** DERIVED from the ledger by a trigger. No form field writes this. */
  spentAmount?: string | number | null
  ownerUserId?: string | null
  partnerName?: string | null
  activeRuleVersion?: number | null
  rulePublishedAt?: string | null
  closedAt?: string | null
  cancelledAt?: string | null
  cancellationReason?: string | null
  /**
   * THE PLAN, and nothing else. Labelled "Planned" on every screen, never
   * combined with a measured value, never an input to any impact figure.
   */
  expectedUpliftPp?: string | number | null
  impactMeasuredAt?: string | null
  spendReconciledAt?: string | null
  spendDiscrepancy?: string | number | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface RuleResponse {
  id: string
  ruleVersion: number
  definition: Record<string, unknown>
  ruleHash: string
  targetGroupKeys: string[]
  matchedEstimate?: number | null
  estimatedAt?: string | null
  publishedAt?: string | null
  publishedBy?: string | null
  note?: string | null
}

/** One field the segment DSL accepts. The rule builder is driven by THIS. */
export interface FieldSpec {
  field: string
  kind: 'ENUM' | 'SHORT' | 'UUID' | 'BOOLEAN' | 'TAG'
  operators: string[]
  /** Allowed values for an enum field, empty otherwise. `name()`, not `getCode()`. */
  values: string[]
}

/**
 * A preset, with the two facts that make it honest: which tags it needs, and
 * how many voters in scope actually carry each of them.
 */
export interface TargetGroupOption {
  key: string
  displayName: string
  description?: string | null
  requiresTag: string[]
  matchingCount: number
  tagCoverage: Record<string, number>
}

/**
 * `voter.api.VoterSummary` — PII-free by construction: no name beyond the
 * display name, no national ID, no phone. Safe to render in a preview.
 */
export interface VoterSampleRow {
  id: string
  displayName?: string | null
  stationId?: string | null
  supportLevel: number
  supportBandCode?: string | null
  confidenceCode?: string | null
}

export interface PreviewResponse {
  count: number
  sample: VoterSampleRow[]
}

export interface EligibilityResponse {
  snapshotId: string
  ruleVersion: number
  ruleHash: string
  eligibleCount: number
  byUnit: Record<string, number>
  byGroup: Record<string, number>
  materialisedCount: number
  /** False when the pool exceeded the shortlist cap — the count is real, the rows are not. */
  materialised: boolean
  computedAt?: string | null
  computeMs?: number | null
  triggerSourceCode: SnapshotTriggerCode
  stale: boolean
  staleReasonCode?: string | null
}

// ── beneficiaries ───────────────────────────────────────────────────────────

/**
 * `voterId` is genuinely nullable. `displayName` comes from the register for a
 * linked row and from the stored name for an unlinked one — one field, two
 * sources, so a list renders uniformly.
 *
 * There is no unmasked phone or national ID on this type, by construction.
 */
export interface BeneficiaryResponse {
  id: string
  programId: string
  programName?: string | null
  voterId?: string | null
  displayName?: string | null
  maskedPhone?: string | null
  maskedNationalId?: string | null
  genderCode?: string | null
  birthYear?: number | null
  statusCode: BeneficiaryStatusCode
  shortlistedAt?: string | null
  enrolledAt?: string | null
  firstDeliveredAt?: string | null
  completedAt?: string | null
  droppedAt?: string | null
  dropReasonCode?: DropReasonCode | null
  dropNote?: string | null
  matchedRuleVersion?: number | null
  /** Field NAMES that matched — never their values. */
  matchedCriteria: string[]
  unitId?: string | null
  unitPath?: string | null
  enrolledByMobilizerId?: string | null
  enrolledByMobilizerName?: string | null
  supportBefore?: number | null
  supportBeforeAt?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

/** The ONE type carrying plaintext, from the ONE audited endpoint. */
export interface BeneficiaryContactResponse {
  id: string
  phone?: string | null
  nationalId?: string | null
}

export interface BeneficiaryEventResponse {
  id: string
  eventTypeCode: BeneficiaryEventTypeCode
  fromStatusCode?: BeneficiaryStatusCode | null
  toStatusCode?: BeneficiaryStatusCode | null
  occurredAt: string
  actorId?: string | null
  actorLabel?: string | null
  mobilizerId?: string | null
  mobilizerName?: string | null
  reasonCode?: string | null
  note?: string | null
}

export interface ShortlistResponse {
  requested: number
  created: number
  alreadyPresent: number
}

export interface BeneficiaryRef {
  beneficiaryId: string
  programId: string
  programName?: string | null
  statusCode: BeneficiaryStatusCode
}

/** `groupId` is SYNTHETIC and per-response. It is never a blind index. */
export interface DuplicateGroupResponse {
  groupId: string
  programmeCount: number
  members: BeneficiaryRef[]
}

export interface DuplicateReportResponse {
  groups: DuplicateGroupResponse[]
  /** Rows with no comparable identity. "0 duplicates" over these is a lie. */
  unmatchableCount: number
}

// ── milestones, KPIs, documents, partners ───────────────────────────────────

export interface MilestoneResponse {
  id: string
  title: string
  description?: string | null
  statusCode: MilestoneStatusCode
  priorityCode: MilestonePriorityCode
  dueOn?: string | null
  completedOn?: string | null
  ownerUserId?: string | null
  sortOrder: number
}

export interface KpiResponse {
  id: string
  code: string
  name: string
  description?: string | null
  unit?: string | null
  directionCode: KpiDirectionCode
  targetValue?: string | number | null
  baselineValue?: string | number | null
  currentValue?: string | number | null
  measuredAt?: string | null
  sourceCode: KpiSourceCode
  /** False for every DERIVED_* source — do not render an input the API will 409. */
  editable: boolean
  /** e.g. "awaiting part 3" for a DERIVED_SPEND KPI. Never a fabricated 0. */
  note?: string | null
  sortOrder: number
}

export interface DocumentResponse {
  id: string
  fileId: string
  title: string
  documentTypeCode: ProgramDocumentTypeCode
  description?: string | null
  sizeBytes?: number | null
  uploadedBy?: string | null
  createdAt?: string | null
}

export interface DocumentReserveResponse { fileId: string; uploadUrl: string }

export interface PartnerResponse {
  id: string
  name: string
  partnerTypeCode: PartnerTypeCode
  contactName?: string | null
  contactEmail?: string | null
  contributionAmount?: string | number | null
  roleNote?: string | null
}

// ── money ───────────────────────────────────────────────────────────────────

export interface DisbursementResponse {
  id: string
  programId: string
  beneficiaryId?: string | null
  beneficiaryLabel?: string | null
  entryTypeCode: DisbursementEntryTypeCode
  /** Signed: positive for a disbursement, negative for a reversal. */
  amount: string | number
  currency: string
  channelCode: DeliveryChannelCode
  occurredOn: string
  recordedAt: string
  recordedBy?: string | null
  mobilizerId?: string | null
  mobilizerName?: string | null
  unitId?: string | null
  unitPath?: string | null
  externalReference?: string | null
  reversesId?: string | null
  reversalReasonCode?: ReversalReasonCode | null
  note?: string | null
}

export interface SpendResponse {
  budgetAmount?: string | number | null
  spentAmount?: string | number | null
  currency?: string | null
  /** NULL when there is no budget. A programme with no budget has no remaining budget. */
  remaining?: string | number | null
  ledgerSum?: string | number | null
  discrepancy?: string | number | null
  reconciledAt?: string | null
  withinTolerance: boolean
  entryCount: number
  reversalCount: number
  /** Spend with no beneficiary attached — a village water tank. */
  unattributedAmount?: string | number | null
  unattributedCount: number
}

// ── impact ──────────────────────────────────────────────────────────────────

/**
 * Per programme. `explanation` is NOT NULL whenever any measurement number is
 * null — a blank cell teaches the reader nothing.
 *
 * `expectedUpliftPp` is the PLAN, carried separately and never an operand.
 */
export interface ImpactSummary {
  verdictCode: ImpactVerdictCode
  totalBeneficiaries: number
  measurable: number
  measured: number
  excludedUnlinked: number
  excludedNoBaseline: number
  excludedNotEnrolled: number
  meanUpliftTreated?: string | number | null
  controlSize: number
  controlMeasured: number
  meanUpliftControl?: string | number | null
  differenceInDifferences?: string | number | null
  explanation?: string | null
  measuredAt?: string | null
  expectedUpliftPp?: string | number | null
}

export interface ImpactRefreshResponse {
  treatedConsidered: number
  treatedMeasured: number
  controlConsidered: number
  controlMeasured: number
  droppedExcludedMethod: number
  droppedUnassessed: number
  droppedNullLevel: number
  summary: ImpactSummary
}

export interface StratumRow { stratum: string; beneficiaries: number; controls: number }

/** Counts, strata and means only. Never a member id — they are non-participants. */
export interface ControlCohortResponse {
  size: number
  measured: number
  drawnAt?: string | null
  stale: boolean
  excludedUnlinked: number
  excludedNotEnrolled: number
  excludedIncompleteFields: number
  skippedThinStrata: number
  strata: StratumRow[]
}

/**
 * Every stage except `dropped` is EVER-REACHED. `coverage`'s numerator is
 * LINKED enrolments only, and `excludedUnlinkedEnrolled` is the gap.
 */
export interface FunnelResponse {
  eligible: number
  shortlisted: number
  enrolled: number
  enrolledLinked: number
  delivered: number
  completed: number
  dropped: number
  excludedUnlinkedEnrolled: number
  coverage?: string | number | null
  enrolmentRate?: string | number | null
  deliveryRate?: string | number | null
  completionRate?: string | number | null
  ratesUnavailableReason?: string | null
  computedAt?: string | null
  eligibilityStale: boolean
  eligibilityStaleReason?: string | null
}

// ── analytics ───────────────────────────────────────────────────────────────

/** On every analytics response. Three fields, always. */
export interface AnalyticsEnvelope {
  asOfDate?: string | null
  computedAt?: string | null
  scopingFamily: ScopingFamilyCode
}

export interface TrendPoint {
  date: string
  enrolled: number
  delivered: number
  completed: number
  dropped: number
  spend: string | number
  enrolledCumulative: number
  spendCumulative: string | number
}

export interface PortfolioOverviewResponse extends AnalyticsEnvelope {
  programmes: number
  byStatus: Record<string, number>
  byCategory: Record<string, number>
  /** The SUM. Renders beside `distinctSubjects`, never instead of it. */
  beneficiaryTotal: number
  /** DE-DUPLICATED people. The number PollSphere cannot produce. */
  distinctSubjects: number
  unmatchableSubjects: number
  enrolledLinkedTotal: number
  eligibleTotal?: number | null
  coverage?: string | number | null
  coverageUnavailableReason?: string | null
  budgetTotal?: string | number | null
  spendTotal?: string | number | null
  spendUnattributed?: string | number | null
  costPerBeneficiary?: string | number | null
  netCostPerUniquePerson?: string | number | null
  currency?: string | null
  currencyMixedReason?: string | null
  trend: TrendPoint[]
}

export interface ProgrammeRef { programId: string; code: string; name: string }

export interface TargetGroupRow {
  groupKey: string
  displayName: string
  requiresTag: string[]
  programmes: number
  activeProgrammes: number
  beneficiaries: number
  eligiblePool?: number | null
  eligibleUnavailableReason?: string | null
  coverage?: string | number | null
  budget?: string | number | null
  delivered?: string | number | null
  spendUnattributed?: string | number | null
  currency?: string | null
  currencyMixedReason?: string | null
  meanUplift?: string | number | null
  measured: number
  measurable: number
  topProgrammes: ProgrammeRef[]
}

export interface TargetGroupTotals {
  programmes: number
  beneficiaries: number
  budget?: string | number | null
  delivered?: string | number | null
  currency?: string | null
  currencyMixedReason?: string | null
}

export interface TargetGroupsResponse extends AnalyticsEnvelope {
  rows: TargetGroupRow[]
  /** A programme in three groups counts ONCE here. */
  deduplicatedTotals: TargetGroupTotals
  /** …and three times here. Both are shown, labelled differently. */
  sumOfRows: TargetGroupTotals
  unmatchableSubjects: number
}

/** Analytics-side unit reference. Distinct from `UnitRef`, which is a mobilizer
 *  assignment and carries `primary`. */
export interface AnalyticsUnitRef { unitId: string; name: string; path?: string | null }

export interface CrossTabCell {
  groupKey: string
  unitId: string
  enrolled: number
  eligible?: number | null
  coverage?: string | number | null
}

export interface CrossTabResponse extends AnalyticsEnvelope {
  cells: CrossTabCell[]
  groupKeys: string[]
  units: AnalyticsUnitRef[]
  truncatedTo: number
  totalAvailable: number
  orderedBy?: string | null
}

export interface OverlapCell {
  programAId: string
  programBId: string
  sharedCount: number
  aCount: number
  bCount: number
  /** shared ÷ union. A count ramp is dominated by the two largest programmes. */
  jaccard: string | number
}

export interface OverlapResponse extends AnalyticsEnvelope {
  cells: OverlapCell[]
  programmes: ProgrammeRef[]
  distinctReach: number
  sumOfBeneficiaries: number
  deduplicationSaving: number
  unmatchableCount: number
  truncatedTo: number
  totalAvailable: number
  /** Says the ranking is global and nightly — a filter subsets it, never re-ranks. */
  orderedBy?: string | null
}

export interface MultiProgrammeRow {
  groupId: string
  programmeCount: number
  members: BeneficiaryRef[]
}

export interface MultiProgrammePage extends AnalyticsEnvelope {
  rows: MultiProgrammeRow[]
  nextCursor?: string | null
  hasMore: boolean
  unmatchableCount: number
}

export interface QuadrantPoint {
  programId: string
  name: string
  categoryCode: ProgramCategoryCode
  costPerBeneficiary?: string | number | null
  uplift?: string | number | null
  upliftStdErr?: string | number | null
  measured: number
  measurable: number
  /** True where a control cohort exists and the figure is a difference-in-differences. */
  usedDid: boolean
  beneficiaries: number
}

export interface UnratedProgramme {
  programId: string
  name: string
  beneficiaries: number
  reasonCode: string
}

export interface QuadrantResponse extends AnalyticsEnvelope {
  points: QuadrantPoint[]
  /** Below the sample gate, or zero-cost on a log axis. Never plotted as a dot. */
  unrated: UnratedProgramme[]
  medianCost?: string | number | null
  medianUplift?: string | number | null
  medianUnavailableReason?: string | null
  costAxisMin?: string | number | null
  costAxisMax?: string | number | null
  costAxisScale: 'LINEAR' | 'LOG'
  upliftAxisMin?: string | number | null
  upliftAxisMax?: string | number | null
  /** Excluded, not coerced to 0. Coercing calls every cheap unmeasured thing a Workhorse. */
  excludedNullUplift: number
  currency?: string | null
  currencyMixedReason?: string | null
}

export interface EcosystemNode {
  nodeId: string
  nodeTypeCode: EcosystemNodeTypeCode
  label: string
  /** Null for a CHANNEL — it is an enum code with no table behind it. */
  entityId?: string | null
  weight: number
}

export interface EcosystemEdge {
  sourceId: string
  targetId: string
  edgeTypeCode: EcosystemEdgeTypeCode
  weight: number
}

/** No positions and no colours. The client lays it out and owns the palette. */
export interface EcosystemResponse extends AnalyticsEnvelope {
  nodes: EcosystemNode[]
  edges: EcosystemEdge[]
  truncatedNodes: number
  truncatedEdges: number
  cap: number
}

export interface ConcentrationRow {
  nodeTypeCode: EcosystemNodeTypeCode
  entityId?: string | null
  label: string
  programmeCount: number
  reasonCode: string
}

export interface ConcentrationResponse extends AnalyticsEnvelope {
  rows: ConcentrationRow[]
}

export interface ProgramCoverageRow {
  unitId: string
  unitName: string
  unitPath?: string | null
  enrolledLinked: number
  eligible?: number | null
  coverage?: string | number | null
  unavailableReason?: string | null
}

export interface ProgramCoverageResponse extends AnalyticsEnvelope {
  rows: ProgramCoverageRow[]
  /** Treats a ward of 200 like a ward of 40,000 — shown beside the weighted one. */
  unweightedMean?: string | number | null
  weightedMean?: string | number | null
  unknownEligibilityCount: number
}

export interface GapsResponse extends AnalyticsEnvelope {
  /** Units with eligible people and ZERO enrolments. The screen's reason to exist. */
  gaps: ProgramCoverageRow[]
  /** Not treated as zero. */
  unknownEligibility: ProgramCoverageRow[]
  truncatedTo: number
  totalAvailable: number
  orderedBy?: string | null
}

export interface GeographyCell {
  unitId: string
  unitName: string
  unitPath?: string | null
  enrolledLinked: number
  eligible?: number | null
  coverage?: string | number | null
  costPerBeneficiary?: string | number | null
  meanUplift?: string | number | null
  measured: number
  measurable: number
  delivered: number
  spendUnattributed?: string | number | null
  currency?: string | null
  currencyMixedReason?: string | null
}

export interface GeographyResponse extends AnalyticsEnvelope {
  cells: GeographyCell[]
  unitTypeCode: string
}

export interface BurnPoint {
  date: string
  planned?: string | number | null
  actual?: string | number | null
  cumulative?: string | number | null
}

export interface CostRow {
  key: string
  beneficiaries: number
  spend?: string | number | null
  costPerBeneficiary?: string | number | null
}

export interface MoneyResponse extends AnalyticsEnvelope {
  burnDown: BurnPoint[]
  burnRatePerDay?: string | number | null
  runwayDays?: number | null
  projectedExhaustion?: string | null
  budgetTotal?: string | number | null
  spendTotal?: string | number | null
  spendUnattributed?: string | number | null
  ledgerSum?: string | number | null
  discrepancy?: string | number | null
  reconciledAt?: string | null
  withinTolerance: boolean
  costByCategory: CostRow[]
  costByChannel: CostRow[]
  portfolioMedianCost?: string | number | null
  medianUnavailableReason?: string | null
  currency?: string | null
  currencyMixedReason?: string | null
}

/** `ran: false` + `notRunReason` is the whole point. There is no classification below the gate. */
export interface RoundNumberResult {
  ran: boolean
  notRunReason?: string | null
  valueCount: number
  roundShare?: string | number | null
}

export interface BenfordResponse extends AnalyticsEnvelope {
  ran: boolean
  notRunReason?: string | null
  valueCount: number
  mad?: string | number | null
  /** Absent when `ran` is false. Its copy says "worth checking", never "fabrication". */
  classificationCode?: string | null
  observed: Record<string, number>
  expected: Record<string, string | number>
  roundNumbers: RoundNumberResult
}

export interface MeasurementBucket {
  monthsSinceEnrolment: number
  meanUplift?: string | number | null
  measured: number
}

export interface BenchmarkRow {
  key: string
  meanUplift?: string | number | null
  measured: number
  programmes: number
}

/**
 * PORTFOLIO impact. No scalar verdict — a portfolio has N verdicts, and
 * `expectedUpliftPp` is absent because nine plans do not combine honestly.
 */
export interface ImpactResponse extends AnalyticsEnvelope {
  byVerdict: Record<string, number>
  programmes: number
  programmesMeasured: number
  totalBeneficiaries: number
  measurable: number
  measured: number
  excludedUnlinked: number
  excludedNoBaseline: number
  excludedNotEnrolled: number
  meanUpliftTreated?: string | number | null
  controlSize: number
  controlMeasured: number
  meanUpliftControl?: string | number | null
  differenceInDifferences?: string | number | null
  explanation?: string | null
  /** Named for what it measures: when the field team re-assessed, not when the effect appeared. */
  upliftByMonthsToMeasurement: MeasurementBucket[]
  categoryBenchmarks: BenchmarkRow[]
  benchmarkUnavailableReason?: string | null
  measuredAt?: string | null
}

export interface EquityIndexRow {
  dimension: string
  value: string
  enrolled: number
  enrolledShare?: string | number | null
  eligible?: number | null
  eligibleShare?: string | number | null
  /** enrolled share ÷ eligible share. 0.4 means badly under-represented. */
  representationIndex?: string | number | null
}

export interface EquityResponse extends AnalyticsEnvelope {
  rows: EquityIndexRow[]
  excludedUnlinked: number
  /** Set when no snapshot carries a dimension breakdown. No index is better than a wrong one. */
  unavailableReason?: string | null
}

export interface CompletenessBlock {
  beneficiaries: number
  unlinked: number
  missingNationalId: number
  missingPhone: number
  missingPostEnrolmentReading: number
  milestonesWithoutOwner: number
  disbursementsWithoutBeneficiary: number
}

export interface DurationPercentile { percentile: string; days?: number | null }

export interface MobilizerPerformanceRow {
  mobilizerId: string
  name: string
  enrolments: number
  deliveries: number
  costPerEnrolment?: string | number | null
  meanUplift?: string | number | null
  measured: number
  dropRate?: string | number | null
  programmes: number
  roundNumberShare?: string | number | null
  currency?: string | null
  currencyMixedReason?: string | null
}

export interface AnomalySummary {
  flaggedTotal: number
  /** The shared-phone check covers only rows that recorded one. */
  beneficiariesWithoutPhone: number
  unmatchableSubjects: number
  velocityRan: boolean
  velocityNotRunReason?: string | null
  roundNumbers: RoundNumberResult
}

export interface QualityResponse extends AnalyticsEnvelope {
  completeness: CompletenessBlock
  timeToDeliver: DurationPercentile[]
  mobilizers: MobilizerPerformanceRow[]
  anomalies: AnomalySummary
}

/** A flag for review with a reason. Never an accusation. */
export interface FlaggedRow {
  reasonCode: string
  subjectTypeCode: string
  subjectId?: string | null
  label: string
  value?: string | number | null
  threshold?: string | number | null
  programmeCount: number
}

export interface FlaggedPage extends AnalyticsEnvelope {
  rows: FlaggedRow[]
  nextCursor?: string | null
  hasMore: boolean
}

export interface AlertRuleResponse {
  id: string
  /** NULL = portfolio-wide. */
  programId?: string | null
  programName?: string | null
  name: string
  metricCode: AlertMetricCode
  comparatorCode: AlertComparatorCode
  threshold: string | number
  windowDays?: number | null
  recipientUserId: string
  recipientEmail?: string | null
  cooldownHours: number
  enabled: boolean
  lastFiredAt?: string | null
  lastValue?: string | number | null
}

export interface ExportJobResponse {
  jobId: string
  exportKindCode: ProgramExportKindCode
  statusCode: ProgramExportStatusCode
  rowCount?: number | null
  /** The CSV's first line says so too — the file outlives this response. */
  truncated: boolean
  resultFileId?: string | null
  downloadUrl?: string | null
  expiresAt?: string | null
  createdAt?: string | null
}

export interface AnalyticsRecomputeResult {
  from: string
  to: string
  programmesTouched: number
  overlapPairs: number
  alertsFired: number
  prunedRollupRows: number
}

// ── live coverage (Phase 5, prompts 41–44) ──────────────────────────────────
//
// NAMING. `CoverageResponse`/`CoverageRow`/`CoverageStatusCode` above belong to
// MOBILIZER coverage — how many agents cover a unit — and `ProgramCoverage*` to
// programme coverage-and-gaps. Neither has anything to do with cameras. Every
// type here is therefore prefixed with the entity it is about, and the ones that
// would have collided (`AssignmentResponse`) carry the feature name.

export interface CoverageSubjectResponse {
  subjectTypeCode: string
  ref: string | null
  label: string | null
  secondaryLabel: string | null
  statusCode: string | null
  /** False when the owning feature could not name it — render the code, never fail. */
  resolved: boolean
}

export interface CoverageFeedResponse {
  id: string
  eventId: string
  title: string
  kindCode: string
  statusCode: string
  broadcasterId: string | null
  broadcasterName: string | null
  deviceLabel: string | null
  startedAt: string | null
  endedAt: string | null
  durationSec: number | null
  peakViewers: number
  totalJoins: number
  liveViewers: number
  recordingStateCode: string
  posterFileId: string | null
  /**
   * Where the DEVICE was when it went on air — not the event's planned venue.
   * Null when the device could not say, in which case
   * `locationUnavailableReason` carries why. Exactly one of the two is ever set.
   */
  capturedLatitude: string | number | null
  capturedLongitude: string | number | null
  /** Metres. What lets a map say "wifi estimate" rather than drawing a confident dot. */
  locationAccuracyM: number | null
  /** When the DEVICE took the fix. Compare with `startedAt` to spot a cached one. */
  locatedAt: string | null
  locationUnavailableReason: string | null
}

export interface CoverageEventResponse {
  id: string
  ref: string
  title: string
  description: string | null
  eventTypeCode: string
  statusCode: string
  classificationCode: string
  unitId: string | null
  unitName: string | null
  unitLabels: string[] | null
  venue: string | null
  latitude: string | number | null
  longitude: string | number | null
  scheduledStart: string
  scheduledEnd: string | null
  startedAt: string | null
  endedAt: string | null
  organiserId: string | null
  organiserName: string | null
  expectedAttendance: number | null
  subjectTypeCode: string | null
  subjectRef: string | null
  subjectSlug: string | null
  subject: CoverageSubjectResponse | null
  roomName: string | null
  /**
   * Populated on list rows as well as the detail response since backend prompt 46
   * §C. It used to be `[]` on a list row, and six screens read it.
   */
  feeds: CoverageFeedResponse[]
  /** Whether THE CALLER has pressed the bell. Per-caller, not a property of the event. */
  watching: boolean
}

export interface LiveFeedResponse {
  feedId: string
  eventId: string
  eventRef: string
  eventTitle: string
  classificationCode: string
  unitName: string | null
  venue: string | null
  feedTitle: string
  kindCode: string
  broadcasterId: string | null
  broadcasterName: string | null
  startedAt: string | null
  liveViewers: number
  peakViewers: number
  /** Running longer than `axis.coverage.max-session-hours` — probably nobody stopped it. */
  liveTooLong: boolean
  roomName: string
  /** The JPEG the egress captured, or null. Presigned via `/coverage/feeds/{id}/poster`. */
  posterFileId: string | null
  /**
   * Where the DEVICE was when it went on air — not the event's planned venue.
   * Null when the device could not say, in which case
   * `locationUnavailableReason` carries why. Exactly one of the two is ever set.
   */
  capturedLatitude: string | number | null
  capturedLongitude: string | number | null
  /** Metres. What lets a map say "wifi estimate" rather than drawing a confident dot. */
  locationAccuracyM: number | null
  /** When the DEVICE took the fix. Compare with `startedAt` to spot a cached one. */
  locatedAt: string | null
  locationUnavailableReason: string | null
}

export interface FeedTokenResponse {
  token: string
  /** What the BROWSER connects to. Never the backend's own LiveKit address. */
  wsUrl: string
  roomName: string
  identity: string
  role: string
  expiresAt: string
}

export interface CoverageRecordingResponse {
  id: string
  eventId: string
  feedId: string | null
  sourceCode: string
  stateCode: string
  fileId: string | null
  posterFileId: string | null
  contentType: string | null
  sizeBytes: number | null
  durationSec: number | null
  startedAt: string | null
  endedAt: string | null
  error: string | null
  capturedBy: string | null
  capturedByName: string | null
}

export interface PlaybackResponse {
  url: string
  expiresAt: string
  contentType: string | null
  sizeBytes: number | null
  durationSec: number | null
}

export interface OfflineRecordingResponse { recordingId: string; uploadUrl: string }

export interface CoverageAssignmentResponse {
  id: string
  eventId: string
  eventRef: string | null
  eventTitle: string | null
  eventScheduledStart: string | null
  feedId: string | null
  userId: string
  userName: string | null
  mobilizerId: string | null
  mobilizerName: string | null
  roleCode: string
  statusCode: string
  note: string | null
  assignedBy: string | null
  assignedByName: string | null
  assignedAt: string
  respondedAt: string | null
  declinedReason: string | null
}

export interface CoverageMarkResponse {
  id: string
  eventId: string
  feedId: string | null
  recordingId: string | null
  kindCode: string
  label: string
  note: string | null
  /** The authority. `offsetSec` is derived from it and is null outside a recording. */
  occurredAt: string
  offsetSec: number | null
  authorId: string | null
  authorName: string | null
}

export interface CoverageChapterResponse {
  offsetSec: number
  label: string
  kindCode: string
  author: string | null
}

export interface CoverageAnalyticsSummary {
  events: number
  eventsCovered: number
  eventsUncovered: number
  coverageRate: string | number | null
  feeds: number
  feedsThatWentLive: number
  liveMinutes: number
  recordings: number
  recordingBytes: number
  distinctWatchers: number
  liveWatchMinutes: number
  replayOpens: number
  /**
   * ALWAYS null, and in the payload on purpose. MinIO serves ranged reads and does
   * not report what was watched, so replay watch time is unknowable. Rendering it
   * as 0 beside a non-zero `replayOpens` would state that 148 people opened a
   * recording and watched none of it.
   */
  replayMinutes: number | null
  asOf: string
}

export interface CoverageGeographyRow {
  unitId: string
  unitName: string
  events: number
  covered: number
  uncovered: number
  coverageRate: string | number | null
  liveMinutes: number
}

export interface CoverageGeographyResponse {
  rows: CoverageGeographyRow[]
  truncated: boolean
  cap: number
}

export interface CoverageWatcherRow {
  userId: string
  name: string | null
  liveMinutes: number
  replayOpens: number
  lastWatchedAt: string | null
}

export interface CoverageMarkSummaryRow {
  eventId: string
  ref: string | null
  title: string | null
  marks: number
  byKind: Record<string, number>
}

export interface CoverageSubjectTypeOption { subjectTypeCode: string; label: string }

/**
 * `GET /programs/{id}/coverage` — the Coverage panel in the programme drawer.
 *
 * NAMED FOR THE PANEL, not for the endpoint. The backend record is
 * `ProgramController.ProgramCoverageResponse`, and that name is already taken in
 * this file by the coverage-and-gaps analytic — which is about eligible people a
 * programme has not reached, and has nothing to do with cameras. Two unrelated
 * things called the same word is how the wrong one gets imported.
 */
export interface ProgramCoveragePanelResponse {
  counts: { events: number; live: number; recordings: number; lastEventAt: string | null }
  events: {
    id: string
    ref: string
    title: string
    eventTypeCode: string
    statusCode: string
    scheduledStart: string
    unitId: string | null
    live: boolean
    recordings: number
  }[]
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Polling Studio (backend feature `survey`, web feature `polling`).
 *
 * The web feature keeps the product name; the backend package is `survey`. Every
 * shape here is the CONTRACT, not a convenience: where the server sends a
 * suppressed estimate with a null value and a sentence, so does this file, and a
 * screen that wants a number has to handle the sentence.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface SurveyResponseDto {
  id: string
  ref: string
  title: string
  description: string | null
  categoryCode: string
  statusCode: string
  purposeCode: string
  audienceCode: string
  frequencyCode: string
  methodologyCode: string
  scopeUnitId: string | null
  scopeUnitName: string | null
  /** Root-first ancestry — "Kenya › Nairobi › Westlands". */
  scopeUnitLabels: string[]
  groupId: string | null
  groupName: string | null
  waveId: string | null
  waveNo: number | null
  /**
   * The whole version, not an id.
   *
   * This shape was previously written here as `currentVersionId` /
   * `currentVersionNo` / `waveLabel` — three fields the server has never sent.
   * The mock invented them too, so every screen that read them worked locally
   * and rendered nothing in production: the Sample screen never loaded an
   * instrument, so a QUESTION-sourced quota dimension could not pick a
   * question, silently. Sixth instance of a fixture certifying a broken client.
   */
  currentVersion: SurveyVersionResponseDto | null
  targetSample: number
  collectedCount: number
  confidenceLevel: number
  targetMoe: string | number | null
  linkedSeatType: string | null
  audioRecordingMode: string
  consentRequired: boolean
  randomiseQuestionOrder: boolean
  randomiseOptionOrder: boolean
  allowOffline: boolean
  /**
   * `OFF` | `SOFT` | `HARD` — whether an interview must be taken where it says
   * it was. SOFT accepts an out-of-area interview and flags it; HARD refuses it
   * at submission. A fence catches honest error — the wrong ward, the phone
   * that never left the office — and not a mock-location app, so no screen may
   * present it as proof of where an interview happened.
   */
  fenceModeCode: string
  /**
   * Metres around a POINT unit. A polling station has coordinates and no
   * polygon, so its fence can only be a radius; a ward has a real boundary and
   * ignores this. Always resolved — the server falls back to the deployment
   * setting, so this is never null and never has to be defaulted here.
   */
  fenceRadiusM: number
  opensAt: string | null
  closesAt: string | null
  launchedAt: string | null
  closedAt: string | null
  ownerUserId: string | null
  ownerName: string | null
  retentionDays: number | null
  /**
   * The edges the server will accept from here, for this caller.
   *
   * The client does not own the state machine. A hardcoded copy drifts the
   * first time an edge changes and offers a transition the server refuses —
   * or, worse, hides one it would have allowed.
   */
  allowedTransitions: string[]
  createdAt: string
  updatedAt: string | null
  version: number
}

export interface SurveyVersionResponseDto {
  id: string
  surveyId: string
  versionNo: number
  stateCode: string
  publishedByName: string | null
  /** The server's answer, not `stateCode !== 'PUBLISHED'` computed here. */
  editable: boolean
  createdAt: string
  publishedAt: string | null
  publishedBy: string | null
  questionCount: number
  checksum: string | null
  changeNote: string | null
  version: number
}

export interface SurveyQuestionOptionDto {
  id: string
  code: string
  label: string
  position: number
  kindCode: string
  exclusive: boolean
  anchorCode: string
  score: string | number | null
  linkedEntity: SurveyLinkedEntityDto | null
  translations: Record<string, string>
}

/** A miss is `resolved: false` with the raw ref — never a null row and never a 500. */
export interface SurveyLinkedEntityDto {
  typeCode: string
  ref: string
  label: string | null
  secondaryLabel: string | null
  resolved: boolean
}

export interface SurveyQuestionDto {
  id: string
  code: string
  text: string
  helpText: string | null
  typeCode: string
  required: boolean
  position: number
  blockId: string | null
  blockCode: string | null
  scaleMin: number | null
  scaleMax: number | null
  numericMin: string | number | null
  numericMax: string | number | null
  minSelections: number | null
  maxSelections: number | null
  randomiseOptions: boolean
  linkedEntity: SurveyLinkedEntityDto | null
  /**
   * The parent of a MATRIX sub-question.
   *
   * A matrix is not one row in the instrument — it is a parent question plus one
   * child per matrix row, each carrying this. It is the only marker of that
   * relationship, so a capture screen that does not read it renders the rows as
   * fourteen unrelated questions.
   */
  parentQuestionId: string | null
  options: SurveyQuestionOptionDto[]
  translations: Record<string, { text: string; helpText: string | null }>
}

export interface SurveySkipRuleDto {
  id: string
  sourceQuestionId: string
  sourceQuestionCode: string | null
  operatorCode: string
  valueJson: string | null
  actionCode: string
  targetQuestionId: string | null
  targetQuestionCode: string | null
  targetBlockId: string | null
  position: number
}

export interface SurveyInstrumentDto {
  surveyId: string
  surveyRef: string
  surveyTitle: string
  version: SurveyVersionResponseDto
  blocks: { id: string; code: string; title: string | null; description: string | null; position: number }[]
  questions: SurveyQuestionDto[]
  skipRules: SurveySkipRuleDto[]
  consentScripts: { id: string; languageCode: string; body: string; audioDisclosure: string | null }[]
  languages: string[]
  randomiseQuestionOrder: boolean
  randomiseOptionOrder: boolean
  audioRecordingMode: string
  consentRequired: boolean
}

/* ── fieldwork ─────────────────────────────────────────────────────────────── */

export interface SurveyAssignmentDto {
  id: string
  surveyId: string
  /** `POLL-2026-0001`. Identifies the poll to a database and to nobody else. */
  surveyRef: string | null
  /**
   * What the poll is CALLED.
   *
   * The interviewer's worklist is a list of assignments and nothing else — no
   * poll picker, no context — so a card headed by a reference tells the person
   * holding the phone nothing about what they are going out to ask. Worse,
   * `surveyRef` was null on that endpoint until the server started resolving the
   * poll for it, so every card read "Poll".
   */
  surveyTitle: string | null
  surveyDescription: string | null
  /** So "go and do this" can be told from "this is paused" before the doorstep. */
  surveyStatusCode: string | null
  mobilizerId: string
  mobilizerName: string | null
  scopeUnitId: string | null
  scopeUnitName: string | null
  stratumId: string | null
  stratumCode: string | null
  targetCount: number
  collected: number
  statusCode: string
  dueAt: string | null
  startedAt: string | null
  completedAt: string | null
  revokedAt: string | null
  revokeReason: string | null
  version: number
}

/**
 * AAPOR rates.
 *
 * Every rate is `null` rather than `0` when its denominator is empty — "0%
 * response rate" and "we have not started" are different statements, and a screen
 * that renders `?? 0` states the first when the server said the second.
 */
export interface SurveyDispositionDto {
  surveyId: string
  complete: number
  partial: number
  refusal: number
  nonContact: number
  otherNonResponse: number
  ineligible: number
  callback: number
  eligibleBase: number
  reachedBase: number
  responseRate1: string | number | null
  responseRate3: string | number | null
  cooperationRate1: string | number | null
  refusalRate1: string | number | null
  contactRate1: string | number | null
  suppressedReason: string | null
}

export interface SurveyFieldProgressDto {
  surveyId: string
  targetSample: number
  collected: number
  assignmentCount: number
  activeAssignments: number
  disposition: SurveyDispositionDto
  assignments: SurveyAssignmentDto[]
}

/* ── design ────────────────────────────────────────────────────────────────── */

export interface SurveyStratumDto {
  id: string
  code: string
  name: string
  unitId: string | null
  unitName: string | null
  populationSize: number | null
  targetN: number
  selectionCode: string
  achieved: number
  version: number
}

export interface SurveyQuotaCellDto {
  id: string
  valueCode: string
  label: string | null
  basisCode: string
  targetN: number | null
  targetShare: string | number | null
  /**
   * The resolved target, whichever basis the cell declares.
   *
   * `target`, not `effectiveTarget`, and `openForCapture`, not `open` — the names
   * are the server's record components verbatim. The client used to invent both,
   * and because the fixture invented them too, every quota cell rendered a target
   * of `—` and an "open" chip that was `undefined` in production and correct in
   * the mock. Fifth time on this project a fixture certified a broken client.
   */
  target: number | null
  achieved: number
  remaining: number | null
  openForCapture: boolean
}

export interface SurveyQuotaDimensionDto {
  id: string
  code: string
  name: string
  sourceCode: string
  questionId: string | null
  questionCode: string | null
  position: number
  cells: SurveyQuotaCellDto[]
}

export interface SurveyQuotaStatusDto {
  surveyId: string
  enforcement: string
  targetSample: number
  collected: number
  dimensions: SurveyQuotaDimensionDto[]
}

/* ── capture ───────────────────────────────────────────────────────────────── */

export interface SurveyResponseSummaryDto {
  id: string
  surveyId: string
  surveyVersionId: string
  assignmentId: string | null
  mobilizerId: string | null
  mobilizerName: string | null
  unitId: string
  unitName: string | null
  respondentNameMask: string | null
  respondentPhoneMask: string | null
  respondentGender: string | null
  respondentAge: number | null
  respondentAgeBand: string | null
  languageCode: string
  statusCode: string
  dispositionCode: string
  consentGiven: boolean
  startedAt: string
  submittedAt: string | null
  durationSeconds: number | null
  qualityScore: string | number | null
  channelCode: string
  offline: boolean
  answeredCount: number
  /** Hidden by a branch. Distinct from `blankCount`, which is shown-and-left-empty. */
  skippedCount: number
  blankCount: number
  version: number
}

/* ── integrity ─────────────────────────────────────────────────────────────── */

export interface SurveyFlagDto {
  id: string
  responseId: string
  ruleCode: string
  severityCode: string
  /** Fields and thresholds. NEVER the respondent's answers. */
  detail: Record<string, unknown>
  raisedAt: string
  resolutionCode: string
  resolvedAt: string | null
  resolvedBy: string | null
  resolutionNote: string | null
  version: number
}

export interface SurveyQueueItemDto {
  responseId: string
  mobilizerId: string | null
  mobilizerName: string | null
  unitName: string | null
  submittedAt: string | null
  durationSeconds: number | null
  statusCode: string
  flags: SurveyFlagDto[]
}

export interface SurveyQualitySummaryDto {
  surveyId: string
  interviews: number
  flaggedInterviews: number
  openFlags: number
  ruleCounts: Record<string, number>
  queue: SurveyQueueItemDto[]
}

export interface SurveyBackCheckDto {
  id: string
  responseId: string
  assignmentId: string | null
  assignedTo: string | null
  statusCode: string
  outcomeCode: string | null
  mismatchCount: number
  conductedAt: string | null
  note: string | null
  version: number
}

/* ── analysis ──────────────────────────────────────────────────────────────── */

/**
 * One published figure — or the sentence explaining why there is none.
 *
 * `suppressed: true` means `estimate` is null and `suppressedSentence` is what to
 * render IN ITS PLACE. There is no fallback number anywhere in this shape.
 */
export interface SurveyEstimateDto {
  id: string
  questionId: string
  questionCode: string | null
  optionId: string | null
  optionCode: string | null
  optionLabel: string | null
  subgroupDimension: string | null
  subgroupValue: string | null
  baseCode: string
  baseN: number
  effectiveN: string | number | null
  estimate: string | number | null
  standardError: string | number | null
  moe95: string | number | null
  ciLower: string | number | null
  ciUpper: string | number | null
  deff: string | number | null
  includesFlagged: boolean
  suppressed: boolean
  suppressionReasonCode: string | null
  suppressedSentence: string | null
  computedAt: string
}

export interface SurveyEstimateSetDto {
  surveyId: string
  weightRunId: string | null
  methodCode: string | null
  converged: boolean | null
  deff: string | number | null
  effectiveN: string | number | null
  baseN: number
  unavailable: string | null
  estimates: SurveyEstimateDto[]
}

export interface SurveyWeightRunDto {
  id: string
  surveyId: string
  surveyVersionId: string
  methodCode: string
  stateCode: string
  startedAt: string | null
  finishedAt: string | null
  iterations: number
  converged: boolean | null
  maxDeviation: string | number | null
  trimAt: string | number | null
  trimmedCount: number
  deff: string | number | null
  effectiveN: string | number | null
  baseN: number
  errorCode: string | null
  targets: { dimensionCode: string; valueCode: string; targetShare: string | number; achievedShare: string | number | null }[]
  version: number
}

export interface SurveyCrosstabCellDto {
  rowOptionId: string | null
  rowLabel: string | null
  colOptionId: string | null
  colLabel: string | null
  observed: number
  expected: string | number | null
  rowPct: string | number | null
  colPct: string | number | null
  totalPct: string | number | null
  stdResidual: string | number | null
}

/** `assumptionsMet: false` means `pValue` and `cramersV` are null and the note speaks. */
export interface SurveyCrosstabDto {
  id: string
  surveyId: string
  rowQuestionId: string
  rowQuestionCode: string
  rowQuestionText: string
  colQuestionId: string
  colQuestionCode: string
  colQuestionText: string
  grandTotal: number
  chiSquare: string | number | null
  df: number | null
  pValue: string | number | null
  cramersV: string | number | null
  assumptionsMet: boolean
  assumptionNote: string | null
  computedAt: string
  cells: SurveyCrosstabCellDto[]
}

export interface SurveySegmentationDto {
  surveyId: string
  k: number
  iterations: number
  converged: boolean
  standardised: boolean
  analysedN: number
  featureCodes: string[]
  segments: { index: number; size: number; centroid: (string | number)[]; centroidZ: (string | number)[] }[]
  note: string | null
}

export interface SurveyPcaDto {
  surveyId: string
  analysedN: number
  onCorrelationMatrix: boolean
  converged: boolean
  sweeps: number
  featureCodes: string[]
  components: { index: number; eigenvalue: string | number; varianceShare: string | number; cumulativeShare: string | number; loadings: (string | number | null)[] }[]
  note: string | null
}

/* ── output ────────────────────────────────────────────────────────────────── */

export interface SurveyExportJobDto {
  id: string
  surveyId: string
  versionId: string | null
  formatCode: string
  piiModeCode: string
  stateCode: string
  rowCount: number
  truncated: boolean
  fileId: string | null
  downloadUrl: string | null
  expiresAt: string | null
  errorCode: string | null
  createdAt: string
  version: number
}

export interface SurveyCodeDto {
  id: string
  questionId: string
  code: string
  label: string
  description: string | null
  parentId: string | null
  position: number
  codedAnswers: number
  version: number
}

export interface SurveyCodeFrameDto {
  surveyId: string
  questionId: string
  questionCode: string
  verbatimCount: number
  codedCount: number
  doubleCodedCount: number
  agreement: string | number | null
  agreementNote: string | null
  codes: SurveyCodeDto[]
}

export interface ExternalPollDto {
  id: string
  pollsterName: string
  pollDate: string
  fieldStart: string | null
  fieldEnd: string | null
  seatType: string | null
  unitId: string | null
  unitName: string | null
  sampleSize: number | null
  methodology: string | null
  moe: string | number | null
  confidenceLevel: number | null
  sourceUrl: string | null
  note: string | null
  results: {
    id: string
    candidateId: string | null
    candidateName: string | null
    partyId: string | null
    partyName: string | null
    label: string | null
    share: string | number
    undecided: boolean
  }[]
  version: number
}

/** The weighting rule is a STRING on the response, and the chart renders it. */
export interface PollOfPollsDto {
  subjectLabel: string
  unitId: string | null
  unitName: string | null
  weightingRule: string
  points: { date: string; source: string; ours: boolean; subject: string; share: string | number; moe: string | number | null; sampleSize: number | null }[]
  averages: { subject: string; share: string | number; contributingPolls: number; earliest: string; latest: string }[]
}

// ═══ politics ════════════════════════════════════════════════════════════════

export interface CoalitionMembershipDto {
  id: string
  partyId: string
  partyCode: string
  partyName: string
  coalitionId: string
  coalitionCode: string
  coalitionName: string
  coalitionColourHex: string | null
  roleCode: string
  joinedOn: string
  leftOn: string | null
  open: boolean
}

export interface PartyDto {
  id: string
  code: string
  name: string
  shortName: string | null
  colourHex: string | null
  symbolFileId: string | null
  statusCode: string
  registeredOn: string | null
  deregisteredOn: string | null
  notes: string | null
  /** The OPEN membership, or null. Closed ones are on the history read. */
  coalition: CoalitionMembershipDto | null
  candidateCount: number
  createdAt: string
  updatedAt: string | null
  version: number
}

export interface CoalitionDto {
  id: string
  code: string
  name: string
  shortName: string | null
  colourHex: string | null
  statusCode: string
  formedOn: string | null
  dissolvedOn: string | null
  notes: string | null
  /** Present on the detail read only — a list row carries counts and nothing else. */
  members: CoalitionMembershipDto[] | null
  candidateCount: number
  createdAt: string
  updatedAt: string | null
  version: number
}

export interface CandidateDto {
  id: string
  fullName: string
  seatTypeCode: string
  /** The unit level this seat requires — the server's rule, not the client's guess. */
  requiredLevel: string | null
  statusCode: string
  incumbent: boolean
  unitId: string
  unitName: string | null
  unitTypeCode: string | null
  unitLabels: string[]
  party: {
    id: string; code: string; name: string; shortName: string | null
    colourHex: string | null; symbolFileId: string | null
  } | null
  coalition: {
    id: string; code: string; name: string; shortName: string | null; colourHex: string | null
  } | null
  /** True when the coalition came through the party rather than being set directly. */
  coalitionDerived: boolean
  photoFileId: string | null
  notes: string | null
  createdAt: string
  updatedAt: string | null
  version: number
}

export interface CandidateRefDto {
  id: string
  fullName: string
  seatTypeCode: string
  statusCode: string
  incumbent: boolean
  unitId: string
  unitLabel: string | null
  party: { id: string; code: string; name: string; shortName: string | null; colourHex: string | null } | null
  coalition: { id: string; code: string; name: string; shortName: string | null; colourHex: string | null } | null
}

export interface SeatTypeDto {
  code: string
  requiredUnitLevel: string | null
}

// ═══ survey — the shapes the first pass did not need ═════════════════════════

/**
 * The crosstab LIST shape. Not the detail shape.
 *
 * No cells, no `surveyId`, no question text. Typing the list as the detail
 * compiles and then hands `undefined` to anything that maps over `cells`.
 */
export interface SurveyCrosstabSummaryDto {
  id: string
  rowQuestionId: string
  rowQuestionCode: string
  colQuestionId: string
  colQuestionCode: string
  grandTotal: number
  chiSquare: string | number | null
  df: number | null
  pValue: string | number | null
  cramersV: string | number | null
  assumptionsMet: boolean
  assumptionNote: string | null
  computedAt: string
}

export interface SurveyContactAttemptDto {
  id: string
  assignmentId: string
  attemptNo: number
  outcomeCode: string
  attemptedAt: string
  unitId: string | null
  note: string | null
}

export interface SurveyWaveDto {
  id: string
  waveNo: number
  label: string | null
  opensAt: string | null
  closesAt: string | null
  pollCount: number
}

export interface SurveyGroupDto {
  id: string
  code: string
  name: string
  description: string | null
  /** Empty on list rows; populated on the detail read. */
  waves: SurveyWaveDto[]
  pollCount: number
  createdAt: string
  version: number
}

export interface SurveyPopulationFrameDto {
  id: string
  dimensionCode: string
  valueCode: string
  populationCount: number
  /** Required by the server. "Weighted to the population" must name the population. */
  sourceNote: string
  asOf: string | null
  version: number
}

/** The answer to "can this poll be weighted", asked while there is time to fix it. */
export interface SurveyDesignReadinessDto {
  strataDeclared: boolean
  strataCount: number
  populationFrameDeclared: boolean
  frameDimensions: string[]
  quotasDeclared: boolean
  quotaCellCount: number
  /** Sentences, server-composed. A screen renders them; it does not compose its own. */
  blockers: string[]
}

export interface SurveyAnswerDto {
  questionId: string
  /** The ANALYSIS name — what the codebook, the export column and every crosstab key on. */
  questionCode: string
  /** The question as it was PUT, from the version this interview was answered against. */
  questionText: string | null
  optionCodes: string[] | null
  /** Index-aligned with `optionCodes`; null for an option deleted since. */
  optionLabels: string[] | null
  number: string | number | null
  bool: boolean | null
  text: string | null
  otherText: string | null
  /** False + a null rule id is "shown and left blank"; false + a rule id is "branched past". */
  answered: boolean
  skippedByRuleId: string | null
}

export interface SurveyResponseDetailDto {
  response: SurveyResponseSummaryDto
  answers: SurveyAnswerDto[]
}

export interface SurveyBatchResultDto {
  created: number
  duplicates: number
  rejected: number
  items: {
    index: number
    idempotencyKey: string
    outcome: string
    responseId: string | null
    problem: string | null
  }[]
}

/**
 * What posting a crew actually did.
 *
 * `fenceNote` is non-null only when the poll's fence is OFF: stations on every
 * assignment and nothing being checked is the one way this reads as fenced
 * while it is not, so the server says so rather than leaving it to the screen.
 */
export interface SurveyCrewReportDto {
  created: SurveyAssignmentDto[]
  refused: {
    mobilizerId: string
    mobilizerName: string | null
    unitId: string
    reason: string
  }[]
  fenceNote: string | null
}

export interface SurveyBundleDto {
  surveyId: string
  surveyRef: string
  surveyTitle: string
  statusCode: string
  methodologyCode: string | null
  scopeUnitId: string
  scopeUnitName: string | null
  targetSample: number
  allowOffline: boolean
  /** Never null — an unfenced poll sends mode `OFF`. */
  fence: SurveyBundleFenceDto
  opensAt: string | null
  closesAt: string | null
  etag: string
  generatedAt: string
  instrument: SurveyInstrumentDto
  quota: SurveyQuotaStatusDto | null
}

/**
 * The fence THIS caller is held to, so the field app can warn before an
 * interview is conducted rather than after.
 *
 * `unitId` is the caller's own posting, not the poll's scope: a county-wide
 * poll fielded station by station is as many fences as there are stations.
 */
export interface SurveyBundleFenceDto {
  modeCode: string
  radiusM: number
  unitId: string | null
  unitName: string | null
  /** Null for a unit with a boundary — see `byBoundary`. */
  centreLat: string | number | null
  centreLng: string | number | null
  /**
   * The unit has a real polygon, so there is no centre to measure a radius
   * against and the device cannot decide locally. Say "checked on submission"
   * rather than treating the null centre as a broken bundle.
   */
  byBoundary: boolean
}

export interface SurveyReservedMediaDto {
  mediaId: string
  fileId: string
  /** A presigned PUT. The bytes never transit the application. */
  uploadUrl: string
  kindCode: string
  maxSizeBytes: number
}

export interface SurveyMediaDto {
  id: string
  responseId: string
  questionId: string | null
  fileId: string
  kindCode: string
  durationSeconds: number | null
  capturedAt: string | null
  checksum: string | null
  statusCode: string
  transcriptStateCode: string
  version: number
}

export interface SurveyMediaListDto {
  responseId: string
  audioRecordingMode: string
  media: SurveyMediaDto[]
}

export interface SurveyMediaPlaybackDto {
  mediaId: string
  url: string
  expiresAt: string
  durationSeconds: number | null
  kindCode: string
}

export interface SurveyInterviewerIntegrityDto {
  mobilizerId: string
  mobilizerName: string | null
  backChecks: number
  verified: number
  partialMismatch: number
  fabricated: number
  unreachable: number
  concernsLast90Days: number
}

/**
 * The served code lists.
 *
 * Every key is a vocabulary the server owns. The client renders labels for these
 * codes; it does not decide what the codes ARE.
 */
export interface SurveyVocabularyDto {
  status: string[]
  category: string[]
  purpose: string[]
  audience: string[]
  frequency: string[]
  methodology: string[]
  questionType: string[]
  optionKind: string[]
  optionAnchor: string[]
  audioRecordingMode: string[]
  /**
   * The five that used to be transcribed in the client.
   *
   * A hand-written list is only dangerous when there is a server list to drift
   * from — these had none, which is why they were tolerable. Five of them was
   * the beginning of a second vocabulary, and PollSphere's second vocabulary is
   * why its dropdowns offered `STATION`, `TRACKING`, `YOUTH` and `RURAL` and
   * matched nothing at all, silently.
   */
  stratumSelection: string[]
  quotaSource: string[]
  quotaBasis: string[]
  /** Added with the Interviews screen — its status filter reads this, never a copy. */
  responseStatus: string[]
  contactOutcome: string[]
  backCheckOutcome: string[]
}

export interface SurveySkipLogicVocabularyDto {
  operator: string[]
  action: string[]
}

// ═══ survey — the two reads backend prompt 60 added ═══════════════════════════

/**
 * A map of where fieldwork happened, aggregated to units.
 *
 * There is no respondent coordinate in this shape and there must never be one.
 * The point returned is the UNIT's centroid; a cell below `minCellBase` is not
 * returned at all, because one interview in a ward plotted with a leading
 * candidate is the respondent.
 */
export interface SurveyGeoDto {
  surveyId: string
  levelCode: string
  /** The floor applied, echoed so the screen can name it rather than guess it. */
  minCellBase: number
  totalInterviews: number
  mappedInterviews: number
  /** No fix, or a fix worse than `survey.gps.max-accuracy-m`. */
  unmappedInterviews: number
  suppressedCells: number
  cells: SurveyGeoCellDto[]
}

export interface SurveyGeoCellDto {
  unitId: string
  unitName: string | null
  unitLabels: string[]
  /** The UNIT's centroid. Never a respondent's fix. */
  centroidLat: string | number | null
  centroidLng: string | number | null
  interviews: number
  medianAccuracyM: string | number | null
  flaggedInterviews: number
  /**
   * Present only when a question was asked for AND the subgroup estimate
   * survived suppression. An absent leader and a leader of zero are different
   * facts, so this is null rather than empty.
   */
  leadingOptionId: string | null
  leadingOptionLabel: string | null
  leadingShare: string | number | null
}

export interface TrackerTrendDto {
  groupId: string
  groupCode: string
  groupName: string
  questionId: string
  questionCode: string
  questionText: string
  /** `EstimateBase` — half the meaning of every number below it. */
  baseCode: string
  /**
   * `IDENTICAL` · `EQUIVALENT` · `WORDING_CHANGED` · `OPTIONS_CHANGED` ·
   * `BASE_CHANGED` · `WEIGHTING_CHANGED` · `UNKNOWN`.
   */
  comparability: string
  /** A full sentence, composed server-side. The client renders it, never one of its own. */
  comparabilityNote: string | null
  waves: TrackerTrendWaveDto[]
}

export interface TrackerTrendWaveDto {
  waveId: string
  waveNo: number
  label: string | null
  opensAt: string | null
  closesAt: string | null
  surveyId: string | null
  surveyRef: string | null
  surveyVersionId: string | null
  versionNo: number | null
  /** Non-null when this wave has no poll, no estimate, or a suppressed one. */
  suppressedSentence: string | null
  points: TrackerTrendPointDto[]
}

export interface TrackerTrendPointDto {
  optionId: string
  optionCode: string
  optionLabel: string
  estimate: string | number | null
  moe95: string | number | null
  baseN: number
  effectiveN: string | number | null
  deff: string | number | null
  /**
   * Change from the previous comparable wave, in percentage points.
   *
   * **Null is the load-bearing value.** It means either end was suppressed, or
   * the two waves' instruments are not comparable — and `comparabilityNote` says
   * which. An implementation that always has a number here has re-created the
   * defect this endpoint exists to avoid.
   */
  deltaPp: string | number | null
  comparedToWaveId: string | null
}

// ── the configurator's setup steps (backend prompt 61) ──────────────────────
//
// Three things a poll needs decided before fieldwork that had no route until
// prompt 61: what it is ABOUT, whether its target sample can deliver the
// precision it declares, and when its next wave opens.

/**
 * One thing a poll is about.
 *
 * `label` is resolved on the SERVER at read time from the feature that owns the
 * entity — it is never stored. PollSphere denormalised its linked labels and
 * they went stale the moment a candidate was renamed. `resolved: false` means
 * the owner could not name it (deleted, or the module is switched off in this
 * deployment); the row still renders, with its type and ref.
 */
export interface SurveyLinkDto {
  id: string
  surveyId: string
  typeCode: string
  ref: string
  label: string | null
  secondaryLabel: string | null
  resolved: boolean
  note: string | null
  createdAt: string
  version: number
}

/**
 * A candidate for linking, already narrowed by the poll's seat type and scope.
 *
 * The narrowing happens in the owning feature, so a Langata MP poll is never
 * offered a presidential candidate. `linked` is the server telling the picker
 * its own checkbox state, so no screen diffs two lists to work it out.
 */
export interface SurveyLinkSuggestionDto {
  typeCode: string
  ref: string
  label: string | null
  secondaryLabel: string | null
  linked: boolean
}

/**
 * A link type this deployment can actually resolve.
 *
 * Served rather than hardcoded, for the reason every vocabulary in this feature
 * is: a client list of six type codes would offer the ones whose owning module
 * is switched off, and picking one produces a link that renders unresolved
 * forever. `searchable: false` means the type has no typeahead — the owner did
 * not implement one, so the picker offers nothing to browse.
 */
export interface SurveyLinkableTypeDto {
  typeCode: string
  searchable: boolean
}

/**
 * Can this target sample deliver the precision the poll claims?
 *
 * `targetMoe` and `confidenceLevel` were stored from the first migration and
 * read by nothing, so a poll could declare ±3% at 99% with a target of 200 and
 * no screen would say the second cannot buy the first.
 *
 * Every field is echoed rather than left for this client to re-derive: the
 * figure on the screen and the figure in the audit entry are then the same one.
 * Nothing in this feature recomputes a statistic — the same rule that governs
 * `/estimates`.
 */
export interface SurveySampleSizeDto {
  /** Null on the standalone calculator, which runs before a poll exists. */
  surveyId: string | null
  requiredN: number
  targetSample: number
  /** `target / required`, as a percentage. 100 or more is adequate. */
  adequacyPct: number
  /** The declared population frame's total, or null when none is declared. */
  populationN: number | null
  /** Whether the finite-population correction was applied. PollSphere omits it. */
  finitePopulationCorrected: boolean
  z: string | number
  marginOfErrorPct: string | number
  confidenceLevel: number
  /** The margin the TARGET actually buys — the number a person is asking for. */
  achievedMoePct: string | number | null
  /** Server-composed. A screen renders it; it does not compose its own. */
  sentence: string
}

/**
 * The recurrence, and the dates it will actually produce.
 *
 * There is no cron field, deliberately: the poll's `frequencyCode` is the only
 * recurrence rule on the server. PollSphere carried a frequency enum, a
 * free-text cron pattern AND a next-run timestamp, executed none of them, and
 * the only way to notice was to wait for a wave that never came.
 */
export interface SurveyScheduleDto {
  surveyId: string
  frequencyCode: string
  /** False for `ONE_TIME`, `EVENT_DRIVEN` and `CONTINUOUS` — no interval. */
  recurring: boolean
  autoWave: boolean
  nextRunAt: string | null
  lastSpawnedAt: string | null
  /** Set when the scheduler created this poll, so a draft explains itself. */
  spawnedFromSurveyId: string | null
  groupId: string | null
  groupName: string | null
  waveId: string | null
  waveNo: number | null
  opensAt: string | null
  closesAt: string | null
  /** How many days early the next wave's draft is created. */
  spawnLeadDays: number
  sentence: string
  /**
   * Why `autoWave` cannot be switched on, or null when it can. A refusal the
   * screen can render BEFORE the toggle is thrown rather than after.
   */
  autoWaveBlocked: string | null
  projectedRuns: string[]
}

/**
 * Who a national ID belongs to — the register first, then eCitizen.
 *
 * `found: false` is a complete answer, not an error, and it is deliberately
 * indistinguishable from the identity service being unreachable: the correct
 * next action is the same either way, which is to ask the respondent.
 *
 * **A LOCAL_REGISTER match carries no phone number.** The register's contact
 * channels are sealed and reach a caller only through `voter:read-pii`, on an
 * endpoint that audits each reveal, so prefilling one from `survey:field` would
 * turn an interviewer's screen into a phone-number oracle over the register. An
 * ECITIZEN_IPRS match does carry one: there is no register row being exposed,
 * and the number is attached to the ID document the respondent just handed over.
 */
export interface RespondentLookupDto {
  found: boolean
  /** `LOCAL_REGISTER` | `ECITIZEN_IPRS`, or null when nothing matched. */
  sourceCode: string | null
  /** Set only on a register match; the interview stores it as `matchedVoterId`. */
  voterId: string | null
  displayName: string | null
  genderCode: string | null
  /** What makes the age derivable without asking for it. */
  birthYear: number | null
  registrationStatusCode: string | null
  mobileNumber: string | null
  mobileVerified: boolean
  /** Composed server-side. The interviewer reads this before accepting a match. */
  sentence: string
}

/**
 * The identifiers behind the masks, for one interview (backend prompt 65).
 *
 * `survey:read-raw` — an authority that sat in the catalogue enforcing nothing
 * until this endpoint existed, so masking was unconditional for everybody
 * including the people holding it.
 *
 * A field that was never collected, or whose key has been rotated away, comes
 * back null — the same shape as an interview that never had one. The mask beside
 * it still renders either way.
 */
export interface SurveyRevealedIdentifiersDto {
  responseId: string
  respondentName: string | null
  respondentPhone: string | null
  respondentNationalId: string | null
}
